import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { FaStar, FaTrash, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { createTransactionConversation } from '../utils/chat';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import SchemaEditor from '../components/SchemaEditor';
import { Editor } from '@tinymce/tinymce-react';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, getImageUrl } from '../utils/api';

// Modern Toast Notification System
const ToastNotification = ({ message, type = 'success', onClose }) => {
  const icons = {
    success: <FaCheckCircle className="text-green-500" />,
    error: <FaTimesCircle className="text-red-500" />,
    info: <FaInfoCircle className="text-blue-500" />,
    warning: <FaExclamationTriangle className="text-yellow-500" />
  };

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800'
  };

  return (
    <div className={`fixed top-4 right-4 z-[9999] max-w-sm w-full animate-slideIn`}>
      <div className={`${colors[type]} border rounded-lg shadow-lg p-4 flex items-center gap-3`}>
        <div className="flex-shrink-0 text-xl">
          {icons[type]}
        </div>
        <div className="flex-1">
          <p className="font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimesCircle />
        </button>
      </div>
    </div>
  );
};

// Modern Confirmation Dialog
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", type = "danger" }) => {
  if (!isOpen) return null;

  const colors = {
    danger: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      button: 'bg-red-600 hover:bg-red-700 text-white',
      icon: 'text-red-500'
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
      icon: 'text-yellow-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
      icon: 'text-blue-500'
    }
  };

  const currentColors = colors[type];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onCancel}></div>
      
      {/* Dialog */}
      <div className={`relative ${currentColors.bg} ${currentColors.border} border rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-slideIn`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-full ${currentColors.bg} flex items-center justify-center`}>
            <FaExclamationTriangle className={`text-3xl ${currentColors.icon}`} />
          </div>
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
          {title}
        </h3>
        
        {/* Message */}
        <p className="text-gray-700 text-center mb-6 leading-relaxed">
          {message}
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-3 ${currentColors.button} font-semibold rounded-xl transition-colors`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modern Input Dialog
const InputDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", placeholder = "", defaultValue = "" }) => {
  const [inputValue, setInputValue] = React.useState(defaultValue);

  React.useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onCancel}></div>
      
      {/* Dialog */}
      <div className="relative bg-white border border-gray-200 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-slideIn">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
            <FaInfoCircle className="text-3xl text-blue-500" />
          </div>
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
          {title}
        </h3>
        
        {/* Message */}
        <p className="text-gray-700 text-center mb-4 leading-relaxed">
          {message}
        </p>

        {/* Input */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 mb-6"
          autoFocus
        />
        
        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => onConfirm(inputValue)}
            className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin status is now controlled by backend role and auth store

const sidebarLinks = [
  { name: 'Dashboard', icon: '📊' },
  { name: 'Users', icon: '👤' },
  { name: 'Listings', icon: '📦' },
  { name: 'Orders', icon: '🛒' },
  { name: 'Payments', icon: '💳' },
  { name: 'Support', icon: '✉' },
  { name: 'Blog', icon: '📝' },
  // { name: 'Categories', icon: '📂' }, // Removed - functionality disabled
  { name: 'SEO', icon: '🔍' },
  { name: 'Security', icon: '🛡️' },
  { name: 'Settings', icon: '⚙️' },
];

// Order status backend-to-display mapping
const orderStatusDisplayNames = {
  'on hold': 'On Hold',
  'processing': 'Processing',
  'completed': 'Completed',
  'refunded': 'Refunded',
  'nuwanisuru': 'Seller Account Transferred to Agent',
  'nuwanis': 'Ready for Account Transfer',
  'cancelled': 'Cancelled',
};

// Backend status options for dropdown
const orderStatusOptions = [
  'on hold',
  'processing',
  'completed',
  'refunded',
  'nuwanisuru',
  'nuwanis',
  'cancelled',
];

// Define allowed platform categories
const platformCategories = ['youtube', 'tiktok', 'facebook', 'instagram'];

const FONT_SIZES = [
  { label: 'Small', value: '0.85em' },
  { label: 'Normal', value: '1em' },
  { label: 'Large', value: '1.25em' },
  { label: 'Extra Large', value: '1.5em' },
];

// Add this new component at the top of the file or in a suitable location
function MetaTagPreview({ seo }) {
  // Google SERP Preview
  const googleTitle = seo.title || 'Page Title';
  const googleDesc = seo.description || 'Meta description will appear here.';
  const googleUrl = seo.canonical || 'https://yourdomain.com/page';

  // Facebook OG Preview
  const ogTitle = seo.ogTitle || googleTitle;
  const ogDesc = seo.ogDescription || googleDesc;
  const ogImage = seo.ogImage || 'https://ui-avatars.com/api/?name=OG+Image&size=600x315';

  // Twitter Card Preview
  const twitterTitle = seo.twitterTitle || ogTitle;
  const twitterDesc = seo.twitterDescription || ogDesc;
  const twitterImage = seo.twitterImage || ogImage;

  return (
    <div style={{ marginTop: 24 }}>
      <h4 className="text-lg font-bold mb-2">SEO Previews</h4>
      {/* Google SERP */}
      <div className="mb-4 p-4 border rounded bg-gray-50">
        <div className="text-xs text-gray-500 mb-1">Google Search Result</div>
        <div className="text-blue-700 text-lg leading-tight">{googleTitle}</div>
        <div className="text-green-700 text-xs">{googleUrl}</div>
        <div className="text-gray-700 text-sm">{googleDesc}</div>
      </div>
      {/* Facebook OG */}
      <div className="mb-4 p-4 border-2 border-blue-700 rounded-xl bg-blue-50 shadow-md" style={{maxWidth: 600}}>
        <div className="flex items-center mb-2">
          <span className="bg-blue-700 text-white text-xs font-bold px-2 py-1 rounded mr-2">Facebook</span>
          <span className="text-xs text-blue-700">Open Graph Card Preview</span>
        </div>
        <div className="relative mb-2">
          <img src={ogImage} alt="OG Preview" className="w-full max-w-md h-32 object-cover rounded border-2 border-blue-300" />
          <span className="absolute top-2 left-2 bg-white/80 text-blue-700 text-xs px-2 py-1 rounded border border-blue-700">OG Image</span>
        </div>
        <div className="text-lg font-bold">{ogTitle}</div>
        <div className="text-gray-700 text-sm">{ogDesc}</div>
      </div>
      {/* Twitter Card */}
      <div className="p-4 border-2 border-blue-400 rounded-xl bg-blue-100 shadow-md" style={{maxWidth: 600}}>
        <div className="flex items-center mb-2">
          <span className="bg-blue-400 text-white text-xs font-bold px-2 py-1 rounded mr-2">Twitter</span>
          <span className="text-xs text-blue-800">Card Preview</span>
        </div>
        <div className="relative mb-2">
          <img src={twitterImage} alt="Twitter Preview" className="w-full max-w-md h-24 object-cover rounded border-2 border-blue-200" />
          <span className="absolute top-2 left-2 bg-white/80 text-blue-400 text-xs px-2 py-1 rounded border border-blue-400">Twitter Image</span>
        </div>
        <div className="text-base font-bold">{twitterTitle}</div>
        <div className="text-gray-700 text-sm">{twitterDesc}</div>
      </div>
    </div>
  );
}

function SectionContent({ 
  section, 
  orders, 
  setOrders, 
  blogPosts, 
  setBlogPosts, 
  listings, 
  setListings, 
  users, 
  setUsers,
  showNotification,
  showConfirmDialog,
  showInputDialog,
  // Security props with default values
  securityTab = 'audit-logs',
  setSecurityTab = () => {},
  securityStats = {},
  auditLogs = [],
  failedLogins = [],
  suspiciousActivities = [],
  adminActions = [],
  securityLoading = false
}) {
  const [orderModal, setOrderModal] = React.useState(null);
  const [blogModal, setBlogModal] = React.useState(false);
  const [newBlog, setNewBlog] = React.useState({ title: '', description: '', image: '', imageFile: null, content: '', status: 'pending', category: '', author: '' });
  const [bulkAction, setBulkAction] = React.useState('');
  const [selectedOrders, setSelectedOrders] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [statusMap, setStatusMap] = React.useState({});
  const [loadingMap, setLoadingMap] = React.useState({});
  const statusOptions = ['published', 'pending', 'riject'];
  const [listingCounts, setListingCounts] = React.useState({ all: 0, published: 0, pending: 0, trash: 0 });
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  // const [categories, setCategories] = React.useState([]); // Removed - not used
  const [selectedListings, setSelectedListings] = React.useState([]);
  const [stockStatusMap, setStockStatusMap] = React.useState({});
  const defaultPaymentMethods = [
    {
      key: 'stripe',
      name: 'Stripe Payment Link',
      description: 'Redirect customers to a Stripe Payment Link based on the total amount.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 48 48"><rect width="48" height="48" rx="8" fill="#635BFF"/><text x="24" y="30" textAnchor="middle" fontSize="18" fill="#fff" fontWeight="bold">Stripe</text></svg>
      ),
      enabled: true,
    },
    {
      key: 'crypto',
      name: 'Cryptomus',
      description: 'To start using Cryptomus, you need to follow a few simple steps. 1) Create an account on our website cryptomus.com and complete the... ',
      icon: (
        <svg width="32" height="32" viewBox="0 0 48 48"><rect width="48" height="48" rx="8" fill="#1A1A1A"/><text x="24" y="30" textAnchor="middle" fontSize="16" fill="#00FFB0" fontWeight="bold">₿</text></svg>
      ),
      enabled: true,
    },
    {
      key: 'binance',
      name: 'Binance Pay',
      description: 'A payment gateway that sends your customers to Binance to pay with cryptocurrency.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 48 48"><rect width="48" height="48" rx="8" fill="#F3BA2F"/><text x="24" y="30" textAnchor="middle" fontSize="16" fill="#181A20" fontWeight="bold">BNB</text></svg>
      ),
      enabled: true,
    },
    {
      key: 'wise',
      name: 'Wise & Bank Transfer',
      description: 'Pay securely via Wise or Bank Transfer.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 48 48"><rect width="48" height="48" rx="8" fill="#00B9FF"/><text x="24" y="30" textAnchor="middle" fontSize="16" fill="#fff" fontWeight="bold">Wise</text></svg>
      ),
      enabled: true,
    },
  ];

  const [paymentMethods, setPaymentMethods] = React.useState(defaultPaymentMethods);
  const [editProduct, setEditProduct] = React.useState(null);
  const [editProductData, setEditProductData] = React.useState(null);
  const [editLoading, setEditLoading] = React.useState(false);
  const [mainImagePreview, setMainImagePreview] = React.useState('');
  const [galleryPreviews, setGalleryPreviews] = React.useState([]);
  const mainImageInputRef = React.useRef();
  const galleryInputRef = React.useRef();
  const [managePaymentModal, setManagePaymentModal] = React.useState(null); // { idx, method }
  const [manageDescription, setManageDescription] = React.useState('');
  const [manageLoading, setManageLoading] = React.useState(false);
  const [manageLinks, setManageLinks] = React.useState('');
  // 2FA state (must be at top level for hooks rules)
  const [twoFAStatus, setTwoFAStatus] = React.useState(null); // null=loading, true=enabled, false=disabled
  const [showEnableModal, setShowEnableModal] = React.useState(false);
  const [showDisableModal, setShowDisableModal] = React.useState(false);
  const [qr, setQr] = React.useState('');
  const [backupCodes, setBackupCodes] = React.useState([]);
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  // Add state for backup codes remaining and regenerate modal
  const [backupCodesRemaining, setBackupCodesRemaining] = React.useState(undefined);
  const [showRegenerateModal, setShowRegenerateModal] = React.useState(false);
  const [regenPassword, setRegenPassword] = React.useState('');
  const [regenCode, setRegenCode] = React.useState('');
  const [regenLoading, setRegenLoading] = React.useState(false);
  const [regenError, setRegenError] = React.useState('');
  const [regenSuccessCodes, setRegenSuccessCodes] = React.useState([]);
  
  // SEO state (must be at top level for hooks rules)
  const [seoActiveTab, setSeoActiveTab] = React.useState('Global SEO');
  const [seoSettings, setSeoSettings] = React.useState({
    siteTitle: 'YTProud - Social Media Account Marketplace',
    siteDescription: 'Buy and sell verified social media accounts including YouTube, TikTok, Instagram, and Facebook pages.',
    siteKeywords: 'youtube channels, tiktok accounts, instagram accounts, facebook pages, social media marketplace',
    defaultTitle: 'YTProud - Social Media Account Marketplace',
    defaultDescription: 'Secure marketplace for buying and selling verified social media accounts',
    defaultKeywords: 'social media, accounts, marketplace, youtube, tiktok, instagram, facebook',
    ogTitle: 'YTProud - Social Media Account Marketplace',
    ogDescription: 'Buy and sell verified social media accounts securely',
    ogImage: '/images/og-default.jpg',
    twitterCard: 'summary_large_image',
    twitterTitle: 'YTProud - Social Media Account Marketplace',
    twitterDescription: 'Buy and sell verified social media accounts securely',
    twitterImage: '/images/twitter-default.jpg',
    googleAnalytics: '',
    googleSearchConsole: '',
    robotsTxt: 'User-agent: *\nAllow: /\nDisallow: /admin\nDisallow: /api\nSitemap: https://ytproud.com/sitemap.xml',
    sitemapEnabled: true,
    canonicalEnabled: true,
    schemaEnabled: true
  });
  const [seoSaving, setSeoSaving] = React.useState(false);
  const [seoSaveMessage, setSeoSaveMessage] = React.useState('');
  const [pageSeoSettings, setPageSeoSettings] = React.useState({ pages: {}, products: {} });
  const [selectedPage, setSelectedPage] = React.useState(null);
  const [pageSeoSaving, setPageSeoSaving] = React.useState(false);
  const [pageSeoSaveMessage, setPageSeoSaveMessage] = React.useState('');
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [importData, setImportData] = React.useState('');
  // Add state for sitemap
  const [sitemapData, setSitemapData] = React.useState([]);
  const [sitemapLoading, setSitemapLoading] = React.useState(false);
  const [sitemapMessage, setSitemapMessage] = React.useState('');
  // Blog state
  const [editBlog, setEditBlog] = React.useState(null);
  const [blogLoading, setBlogLoading] = React.useState(false);
  const [blogError, setBlogError] = React.useState('');
  const [blogPostsState, setBlogPostsState] = React.useState([]);

  React.useEffect(() => {
    if (section === 'Settings') {
      setTwoFAStatus(null);
      apiGet('/api/auth/me')
        .then(res => res.json())
        .then(data => {
          setTwoFAStatus(!!data.twoFAEnabled);
          setBackupCodesRemaining(data.backupCodesRemaining);
        })
        .catch(() => { setTwoFAStatus(false); setBackupCodesRemaining(undefined); });
    }
  }, [section, showEnableModal, showDisableModal, showRegenerateModal]);

  React.useEffect(() => {
    apiGet('/api/products/counts').then(r => r.json()).then(setListingCounts);
    // fetch('/api/products/categories').then(r => r.json()).then(setCategories); // Removed due to API errors
  }, []);

  React.useEffect(() => {
    let url = `/api/products?status=${statusFilter}`;
    apiGet(url)
      .then(res => res.json())
      .then(data => {
        let products = data.products || data;
        // Filter by platform category if selected
        if (categoryFilter) {
          products = products.filter(p =>
            (p.accountType && p.accountType.toLowerCase() === categoryFilter) ||
            (p.stats?.category && p.stats.category.toLowerCase() === categoryFilter)
          );
        }
        setListings(products);
      })
      .catch(() => setListings([]));
  }, [statusFilter, categoryFilter, setListings]);

  React.useEffect(() => {
    if (section === 'Users') {
      apiGet('/api/users?limit=50')
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to fetch users');
          }
          return res.json();
        })
        .then(data => {
          // Handle different response formats
          if (data && Array.isArray(data)) {
            setUsers(data);
          } else if (data && data.users && Array.isArray(data.users)) {
            setUsers(data.users);
          } else {
            console.warn('Unexpected users data format:', data);
            setUsers([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          if (error.name === 'TimeoutError') {
            console.error('Users API request timed out');
          }
          setUsers([]);
        });
    }
  }, [section]);

  React.useEffect(() => {
    apiGet('/api/payment-methods')
      .then(res => res.json())
      .then(methods => {
        if (methods && methods.length > 0) {
          setPaymentMethods(defaultPaymentMethods.map(local => {
            const matched = methods.find(m => m.key === local.key);
            return {
              ...local,
              enabled: matched?.enabled ?? local.enabled,
              description: matched?.description ?? local.description,
              links: matched?.links ?? local.links,
            };
          }));
        }
      });
  }, []);

  // Load SEO settings when SEO section is active
  React.useEffect(() => {
    if (section === 'SEO') {
      // Load global SEO settings
      apiGet('/api/seo')
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) {
            setSeoSettings(data);
          }
        })
        .catch(error => {
          console.error('Error loading SEO settings:', error);
        });
      
      // Load page SEO settings
      apiGet('/api/page-seo')
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) {
            setPageSeoSettings(data);
          }
        })
        .catch(error => {
          console.error('Error loading page SEO settings:', error);
        });
    }
  }, [section]);

  React.useEffect(() => {
    if (seoActiveTab === 'Sitemap') {
      setSitemapLoading(true);
      apiGet('/api/sitemap/preview')
        .then(res => res.json())
        .then(data => {
          setSitemapData(data.sitemap || []);
          setSitemapLoading(false);
        })
        .catch(() => setSitemapLoading(false));
    }
  }, [seoActiveTab]);

  React.useEffect(() => {
    if (section === 'Blog') {
      setBlogLoading(true);
      apiGet('/api/blog?status=all')
        .then(res => res.json())
        .then(data => {
          setBlogPostsState(Array.isArray(data) ? data : data.posts || []);
          setBlogLoading(false);
        })
        .catch(() => {
          setBlogPostsState([]);
          setBlogLoading(false);
        });
    }
  }, [section]);

  const handleBulkApplyListings = async () => {
    if (!bulkAction || selectedListings.length === 0) return;
    const action = bulkAction;
    try {
      await apiPatch('/api/products/bulk', { ids: selectedListings, action });
      setSelectedListings([]);
      setBulkAction('');
      showNotification(`Bulk action "${action}" applied successfully! 🎉`, 'success');
      
      // Refresh listings after bulk action
      let url = `/api/products?status=${statusFilter}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      const res = await apiGet(url);
      const data = await res.json();
      setListings(data.products || data);
      
      const countsRes = await apiGet('/api/products/counts');
      const counts = await countsRes.json();
      setListingCounts(counts);
    } catch (error) {
      console.error('Bulk action failed:', error);
      showNotification(`Bulk action failed: ${error.message}`, 'error');
    }
  };

  const handleToggleBoosted = async (id) => {
                    await apiPatch(`/api/products/${id}/boost`);
            setListings(listings => Array.isArray(listings) ? listings.map(l => l._id === id ? { ...l, boosted: !l.boosted } : l) : []);
  };

  // Handle order status change
  const handleOrderStatusChange = (id, status) => {
    setOrders(orders.map(o => o.id === id ? { ...o, status } : o));
  };

  // Handle blog image upload (preview only)
  const handleBlogImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewBlog(nb => ({ ...nb, imageFile: file }));
      const reader = new FileReader();
      reader.onload = ev => setNewBlog(nb => ({ ...nb, image: ev.target.result }));
      reader.readAsDataURL(file);
    }
  };

  // Add new blog post
  const handleAddBlog = async (e) => {
    e.preventDefault();
    setBlogLoading(true);
    setBlogError('');
    try {
      const formData = new FormData();
      formData.append('title', newBlog.title);
      formData.append('description', newBlog.description);
      formData.append('content', newBlog.content);
      formData.append('status', newBlog.status);
      formData.append('category', newBlog.category);
      formData.append('author', newBlog.author);
      if (newBlog.imageFile) formData.append('image', newBlog.imageFile);
      const res = await apiPost('/api/blog', formData);
      if (!res.ok) throw new Error('Failed to add blog post');
    setBlogModal(false);
      setNewBlog({ title: '', description: '', image: '', imageFile: null, content: '', status: 'pending', category: '', author: '' });
      // Refresh blog posts
      const posts = await apiGet('/api/blog?status=all').then(r => r.json());
      setBlogPostsState(Array.isArray(posts) ? posts : posts.posts || []);
    } catch (err) {
      setBlogError('Failed to add blog post.');
    } finally {
      setBlogLoading(false);
    }
  };

  // Edit blog post
  const handleEditBlog = (blog) => {
    setEditBlog(blog);
    setNewBlog({
      title: blog.title,
      description: blog.description,
      image: blog.image,
      imageFile: null,
      content: blog.content,
      status: blog.status,
      category: blog.category,
      author: blog.author
    });
    setBlogModal(true);
  };

  // Update blog post
  const handleUpdateBlog = async (e) => {
    e.preventDefault();
    setBlogLoading(true);
    setBlogError('');
    try {
      const formData = new FormData();
      formData.append('title', newBlog.title);
      formData.append('description', newBlog.description);
      formData.append('content', newBlog.content);
      formData.append('status', newBlog.status);
      formData.append('category', newBlog.category);
      formData.append('author', newBlog.author);
      if (newBlog.imageFile) formData.append('image', newBlog.imageFile);
      const res = await apiPatch(`/api/blog/${editBlog._id}`, formData);
      if (!res.ok) throw new Error('Failed to update blog post');
      setBlogModal(false);
      setEditBlog(null);
      setNewBlog({ title: '', description: '', image: '', imageFile: null, content: '', status: 'pending', category: '', author: '' });
      // Refresh blog posts
      const posts = await apiGet('/api/blog?status=all').then(r => r.json());
      setBlogPostsState(Array.isArray(posts) ? posts : posts.posts || []);
    } catch (err) {
      setBlogError('Failed to update blog post.');
    } finally {
      setBlogLoading(false);
    }
  };

  // Delete blog post
  const handleDeleteBlog = async (id) => {
    const blogPost = blogPosts.find(blog => blog._id === id);
    showConfirmDialog(
      'Delete Blog Post',
      `Are you sure you want to delete "${blogPost?.title || 'this blog post'}"? This action cannot be undone.`,
      async () => {
        setBlogLoading(true);
        try {
          await apiDelete(`/api/blog/${id}`);
          // Refresh blog posts
          const posts = await apiGet('/api/blog?status=all').then(r => r.json());
          setBlogPostsState(Array.isArray(posts) ? posts : posts.posts || []);
          showNotification('Blog post deleted successfully! 🗑️', 'success');
        } catch (err) {
          setBlogError('Failed to delete blog post.');
          showNotification('Failed to delete blog post.', 'error');
        } finally {
          setBlogLoading(false);
        }
      },
      {
        confirmText: 'Delete Blog Post',
        cancelText: 'Cancel',
        type: 'danger'
      }
    );
  };

  // Change status
  const handleBlogStatusChange = async (id, status) => {
    setBlogLoading(true);
    try {
      await apiPatch(`/api/blog/${id}`, { status });
      // Refresh blog posts
      const posts = await apiGet('/api/blog?status=all').then(r => r.json());
      setBlogPostsState(Array.isArray(posts) ? posts : posts.posts || []);
    } catch (err) {
      setBlogError('Failed to update status.');
    } finally {
      setBlogLoading(false);
    }
  };

  // Filtered orders
  const filteredOrders = orders.filter(o =>
    o.orderCode.toLowerCase().includes(search.toLowerCase()) ||
    o.user.toLowerCase().includes(search.toLowerCase())
  );

  const handleStatusChange = (id, value) => {
    setStatusMap(prev => ({ ...prev, [id]: value }));
  };

  const handleUpdateStatus = async (id) => {
    setLoadingMap(prev => ({ ...prev, [id]: true }));
    try {
      const res = await apiPatch(`/api/products/${id}`, { status: statusMap[id] });
      if (res.ok) {
        // Update the local listing status
        setListings(listings => Array.isArray(listings) ? listings.map(l => l._id === id ? { ...l, status: statusMap[id] } : l) : []);
      }
    } finally {
      setLoadingMap(prev => ({ ...prev, [id]: false }));
    }
  };

  // Add a delete handler function
  const handleDeleteListing = async (id) => {
    try {
      const res = await apiDelete(`/api/products/${id}`);
      if (res.ok) {
        setListings(listings => listings.filter(l => l._id !== id));
      }
    } catch (err) {
      console.error('Error deleting listing:', err);
    }
  };

  React.useEffect(() => {
    // Initialize statusMap when listings change
    if (Array.isArray(listings)) {
      setStatusMap(Object.fromEntries(listings.map(l => [l._id, l.status])));
    }
  }, [listings]);

  const handleBulkApply = async () => {
    if (bulkAction === 'delete' && selectedOrders.length) {
      showConfirmDialog(
        'Delete Orders',
        `Are you sure you want to permanently delete ${selectedOrders.length} selected order(s)? This action cannot be undone.`,
        async () => {
          try {
            // Delete each selected order by _id
            await Promise.all(selectedOrders.map(async (_id) => {
              const res = await apiDelete(`/api/orders/${_id}`);
              if (!res.ok) throw new Error('Failed to delete order: ' + _id);
            }));
            setOrders(orders => orders.filter(o => !selectedOrders.includes(o._id)));
            setSelectedOrders([]);
            setBulkAction('');
            showNotification(`Successfully deleted ${selectedOrders.length} order(s)! 🗑️`, 'success');
          } catch (err) {
            showNotification('Failed to delete one or more orders.', 'error');
          }
        },
        {
          confirmText: 'Delete Orders',
          cancelText: 'Cancel',
          type: 'danger'
        }
      );
      return;
    }
    if (bulkAction && selectedOrders.length) {
      if (bulkAction.startsWith('Change status to ')) {
        const newStatus = bulkAction.replace('Change status to ', '');
        
        try {
          // Update MongoDB for each selected order
          await Promise.all(selectedOrders.map(async (orderId) => {
            const order = orders.find(o => o.id === orderId);
            if (order && order.order_id) {
              const res = await apiPatch(`/api/orders/${order.order_id}`, {
                status: newStatus
              });
              if (!res.ok) throw new Error(`Failed to update order ${order.order_id}`);
            }
          }));

          // Update Firestore for each selected order
          await Promise.all(selectedOrders.map(async (orderId) => {
            const order = orders.find(o => o.id === orderId);
            if (order && order.order_id) {
              try {
                const conversationsRef = collection(db, 'sellerbuyerconversations');
                const q = query(conversationsRef, where('order_id', '==', order.order_id));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                  const conversationDoc = querySnapshot.docs[0];
                  const conversationRef = doc(db, 'sellerbuyerconversations', conversationDoc.id);
                  await updateDoc(conversationRef, { status: newStatus });
                  
                  // Update the order message status
                  const messagesCol = collection(db, 'sellerbuyerconversations', conversationDoc.id, 'messages');
                  const messageQuery = query(messagesCol, where('order_id', '==', order.order_id));
                  const messageSnapshot = await getDocs(messageQuery);
                  if (!messageSnapshot.empty) {
                    const messageDoc = messageSnapshot.docs[0];
                    await updateDoc(doc(messagesCol, messageDoc.id), { status: newStatus });
                  }
                }
              } catch (error) {
                console.error(`Error updating Firestore for order ${order.order_id}:`, error);
              }
            }
          }));

          // Update local state
          setOrders(orders.map(o => selectedOrders.includes(o.id) ? { ...o, status: newStatus } : o));
          setSelectedOrders([]);
          setBulkAction('');
          showNotification(`Successfully updated ${selectedOrders.length} order(s) to ${newStatus}! 🎉`, 'success');
        } catch (err) {
          console.error('Error in bulk status update:', err);
          showNotification('Failed to update one or more orders. Please try again.', 'error');
        }
      }
      // Add more actions as needed
    }
  };

  const handleTogglePayment = async idx => {
    try {
      const updated = paymentMethods.map((m, i) =>
        i === idx ? { ...m, enabled: !m.enabled } : m
      );
      
      const res = await apiPatch('/api/payment-methods', { 
        methods: updated.map(({ key, enabled, description, links }) => ({ key, enabled, description, links })) 
      });
      
      if (res.ok) {
        setPaymentMethods(updated);
        const method = updated[idx];
        showNotification(`${method.key} ${method.enabled ? 'enabled' : 'disabled'} successfully! 💳`, 'success');
      } else {
        showNotification('Failed to update payment method.', 'error');
      }
    } catch (error) {
      console.error('Payment method toggle failed:', error);
      showNotification('Failed to update payment method.', 'error');
    }
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setMainImagePreview(ev.target.result);
        setEditProductData(d => ({ ...d, mainImage: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryChange = (e) => {
    let files = Array.from(e.target.files);
    if (files.length < 3) {
              showNotification('Please select at least 3 images.', 'warning');
      return;
    }
    if (files.length > 15) {
      files = files.slice(0, 15);
              showNotification('You can select up to 15 images only.', 'warning');
    }
    Promise.all(files.map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ file, preview: ev.target.result });
        reader.readAsDataURL(file);
      });
    })).then(results => {
      setGalleryPreviews(results.map(r => r.preview));
      setEditProductData(d => ({ ...d, galleryImages: results.map(r => r.file) }));
    });
  };

  const handleRegenerateSitemap = async () => {
    setSitemapLoading(true);
    setSitemapMessage('');
    try {
      const res = await apiPost('/api/sitemap/regenerate');
      const data = await res.json();
      setSitemapMessage(data.message || 'Regenerated!');
      // Refresh preview
      const preview = await apiGet('/api/sitemap/preview');
      const previewData = await preview.json();
      setSitemapData(previewData.sitemap || []);
    } catch {
      setSitemapMessage('Error regenerating sitemap');
    } finally {
      setSitemapLoading(false);
      setTimeout(() => setSitemapMessage(''), 3000);
    }
  };

  const handleSaveAllPageSeo = async () => {
    setPageSeoSaving(true);
    setPageSeoSaveMessage("");
    try {
      let errorCount = 0;
      for (const [pagePath, seoSettings] of Object.entries(pageSeoSettings.pages)) {
        const res = await apiPost("/api/page-seo", { pagePath, seoSettings });
        const data = await res.json();
        if (!res.ok || data.error) errorCount++;
      }
      if (errorCount === 0) {
        setPageSeoSaveMessage("Saved all pages successfully!");
      } else {
        setPageSeoSaveMessage(`Error saving ${errorCount} page(s).`);
      }
      // Refresh state from backend
      const fresh = await apiGet("/api/page-seo");
      setPageSeoSettings(await fresh.json());
    } catch (err) {
      setPageSeoSaveMessage("Error saving: " + err.message);
    } finally {
      setPageSeoSaving(false);
    }
  };

  // ... inside SectionContent, after all useState hooks and before return ...

  switch (section) {
    case 'Users':
      return (
        <div className="w-full">
          <div className="overflow-x-auto w-full">
            <table className="min-w-full w-full bg-white rounded-xl shadow">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="px-4 py-2">Avatar</th>
                  <th className="px-4 py-2">Username</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Registered</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(users) && users.map(u => (
                  <tr key={u._id} className={`border-b last:border-b-0 ${u.isBanned ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-2">
                      {u.avatar ? (
                        <img src={u.avatar} alt="avatar" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {(u.username || u.email || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{u.username}</td>
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2">
                      {u.isBanned ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Banned
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-2">
                      {u.isBanned ? (
                        <button
                          onClick={() => {
                            showConfirmDialog(
                              'Unban User',
                              `Are you sure you want to unban "${u.username}"? This will restore their account access.`,
                              async () => {
                                try {
                                  const response = await apiPatch(`/api/users/${u._id}/unban`);
                                  if (response.ok) {
                                    setUsers(Array.isArray(users) ? users.map(user => 
                                      user._id === u._id ? { ...user, isBanned: false } : user
                                    ) : []);
                                    showNotification(`Successfully unbanned ${u.username}! ✅`, 'success');
                                  }
                                } catch (error) {
                                  console.error('Error unbanning user:', error);
                                  showNotification('Failed to unban user.', 'error');
                                }
                              },
                              {
                                confirmText: 'Unban User',
                                cancelText: 'Cancel',
                                type: 'info'
                              }
                            );
                          }}
                          className="px-3 py-1 rounded bg-green-600 text-white text-sm font-semibold shadow hover:bg-green-700 transition"
                        >
                          Unban
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            showInputDialog(
                              'Ban User',
                              `Enter a reason for banning "${u.username}" (optional):`,
                              async (reason) => {
                                try {
                                  const response = await apiPatch(`/api/users/${u._id}/ban`, { reason: reason || 'No reason provided' });
                                  if (response.ok) {
                                    setUsers(Array.isArray(users) ? users.map(user => 
                                      user._id === u._id ? { ...user, isBanned: true, banReason: reason || 'No reason provided' } : user
                                    ) : []);
                                    showNotification(`Successfully banned ${u.username}! 🚫`, 'success');
                                  }
                                } catch (error) {
                                  console.error('Error banning user:', error);
                                  showNotification('Failed to ban user.', 'error');
                                }
                              },
                              {
                                confirmText: 'Ban User',
                                cancelText: 'Cancel',
                                placeholder: 'Enter ban reason (optional)',
                                defaultValue: ''
                              }
                            );
                          }}
                          className="px-3 py-1 rounded bg-red-600 text-white text-sm font-semibold shadow hover:bg-red-700 transition"
                        >
                          Ban
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    case 'Listings':
      return (
        <div className="w-full">
          {/* Top filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6 mt-6">
            {['all', 'published', 'pending', 'trash'].map(key => (
              <button
                key={key}
                className={`px-5 py-2 rounded-xl font-semibold text-base transition shadow-sm border
                  ${statusFilter === key
                    ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white border-blue-500 shadow-md'
                    : 'bg-white text-blue-700 border-gray-200 hover:bg-blue-50 hover:text-blue-900'}
                `}
                onClick={() => setStatusFilter(key)}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
                <span className={`ml-2 text-xs font-bold ${statusFilter === key ? 'text-white' : 'text-blue-500'}`}>({listingCounts[key] || 0})</span>
              </button>
            ))}
          </div>
          {/* Filter & bulk actions bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4 bg-white rounded-xl shadow px-6 py-4 border border-gray-100">
                                    <input type="checkbox" checked={Array.isArray(listings) && selectedListings.length === listings.length && listings.length > 0} onChange={e => setSelectedListings(e.target.checked ? (Array.isArray(listings) ? listings.map(l => l._id) : []) : [])} className="w-5 h-5 accent-blue-600" />
            <select className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 shadow-sm focus:ring-2 focus:ring-blue-200 font-semibold text-gray-700 transition" value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
              <option value="">Bulk actions</option>
              <option value="delete">Delete</option>
              <option value="publish">Publish</option>
              <option value="pending">Pending</option>
            </select>
            <button className="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold shadow hover:from-blue-700 hover:to-blue-500 transition text-base ml-2" onClick={handleBulkApplyListings}>Apply</button>
            <select className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 shadow-sm focus:ring-2 focus:ring-blue-200 font-semibold text-gray-700 transition ml-4" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="">All Categories</option>
              {platformCategories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>
          {/* Listings table */}
          <div className="overflow-x-auto w-full">
            <table className="w-full bg-white rounded-xl shadow">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                                      <th className="px-4 py-2"><input type="checkbox" checked={Array.isArray(listings) && selectedListings.length === listings.length && listings.length > 0} onChange={e => setSelectedListings(e.target.checked ? (Array.isArray(listings) ? listings.map(l => l._id) : []) : [])} className="w-5 h-5 accent-blue-600" /></th>
                  <th className="px-4 py-2">Image</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Author</th>
                  {/* <th className="px-4 py-2">SKU</th> */}
                  <th className="px-4 py-2">Stock</th>
                  <th className="px-4 py-2">Price</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Boost</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!Array.isArray(listings) || listings.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-4">No listings found.</td></tr>
                ) : (
                  listings.map(l => (
                    <tr key={l._id} className="border-b last:border-b-0">
                      <td className="px-4 py-2"><input type="checkbox" checked={selectedListings.includes(l._id)} onChange={e => setSelectedListings(e.target.checked ? [...selectedListings, l._id] : selectedListings.filter(id => id !== l._id))} className="w-5 h-5 accent-blue-600" /></td>
                      <td className="px-4 py-2">
                        {l.mainImage ? (
                          <img src={getImageUrl(l.mainImage)} alt="Product" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-bold text-xs border border-gray-200">
                            P
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 font-medium"><a href={`/product/${l._id}`} className="text-blue-700 underline" target="_blank" rel="noopener noreferrer">{l.title && l.title.length > 10 ? l.title.slice(0, 10) + '...' : l.title}</a></td>
                      <td className="px-4 py-2">
                        {l.userId?.username ? (
                          <a
                            href={`/profile/${l.userId.username}`}
                            className="text-blue-700 underline hover:text-blue-900"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {l.userId.username}
                          </a>
                        ) : l.userId?.email ? (
                          <a
                            href={`/profile/${l.userId._id}`}
                            className="text-blue-700 underline hover:text-blue-900"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {l.userId.email}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      {/* <td className="px-4 py-2">{l.sku || '-'}</td> */}
                      <td className="px-4 py-2">
                        {l.stockStatus === 'outofstock' ? (
                          <span className="inline-block px-4 py-1 rounded-full bg-red-100 text-red-700 font-semibold text-sm shadow">Out of Stock</span>
                        ) : (
                          <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-sm shadow">In Stock</span>
                        )}
                      </td>
                      <td className="px-4 py-2">{l.salePrice ? <><span className="line-through text-gray-400 mr-1">${l.price}</span> <span className="text-green-600 font-bold">${l.salePrice}</span></> : <>${l.price}</>}</td>
                      <td className="px-4 py-2">{
                        (() => {
                          const platform = (l.accountType || l.platform || '').toLowerCase();
                          if (["youtube", "tiktok", "facebook", "instagram"].includes(platform)) {
                            return platform.charAt(0).toUpperCase() + platform.slice(1);
                          }
                          return '-';
                        })()
                      }</td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => handleToggleBoosted(l._id)} className="focus:outline-none">
                          {l.boosted ? (
                            <span title="Boosted" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white shadow-lg">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
                            </span>
                          ) : (
                            <span title="Click to Boost" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-blue-400 hover:bg-blue-100 hover:text-blue-600 transition">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2">{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-2">
                        <select
                          className="border rounded px-2 py-1"
                          value={statusMap[l._id] || l.status}
                          onChange={async e => {
                            const newStatus = e.target.value;
                            setStatusMap(prev => ({ ...prev, [l._id]: newStatus }));
                            // Auto-update status in backend (use JSON, not FormData)
                            await apiPatch(`/api/products/${l._id}`, { status: newStatus });
                            setListings(listings => Array.isArray(listings) ? listings.map(item => item._id === l._id ? { ...item, status: newStatus } : item) : []);
                          }}
                        >
                          {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        <span className={`ml-2 px-3 py-1 rounded-full text-xs font-bold ${l.status === 'published' ? 'bg-green-100 text-green-700' : l.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : l.status === 'trash' ? 'bg-gray-300 text-gray-700' : 'bg-gray-200 text-gray-700'}`}>{statusMap[l._id] || l.status}</span>
                      </td>
                    <td className="px-4 py-2">
                        <button
                          className="px-4 py-1 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition"
                          onClick={() => {
                            setEditProduct(l);
                            setEditProductData({ ...l });
                          }}
                        >
                          Edit
                        </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Edit Product Modal */}
          {editProduct && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition"
                  onClick={() => setEditProduct(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
                <h3 className="text-2xl font-bold mb-6">Edit Product</h3>
                <form onSubmit={async e => {
                  e.preventDefault();
                  setEditLoading(true);
                  try {
                    // Ensure quantity is at least 1 if stockStatus is instock
                    if (editProductData.stockStatus === 'instock' && (!editProductData.quantity || editProductData.quantity <= 0)) {
                      editProductData.quantity = 1;
                    }
                    // Check if we have new files to upload
                    const hasNewFiles = (editProductData.mainImage && editProductData.mainImage instanceof File) ||
                      (editProductData.galleryImages && editProductData.galleryImages.some(img => img instanceof File));
                    
                    let res;
                    if (hasNewFiles) {
                      // Use FormData for file uploads
                      const formData = new FormData();
                      Object.entries(editProductData).forEach(([key, value]) => {
                        if (key === 'mainImage' && value instanceof File) {
                          formData.append('mainImage', value);
                        } else if (key === 'galleryImages' && Array.isArray(value)) {
                          value.forEach(file => formData.append('galleryImages', file));
                        } else if (key === 'stats') {
                          Object.entries(value).forEach(([statKey, statValue]) => {
                            formData.append(`stats[${statKey}]`, statValue);
                          });
                        } else {
                          formData.append(key, value);
                        }
                      });
                      res = await apiPatch(`/api/products/${editProduct._id}`, formData);
                    } else {
                      // Use JSON for text-only updates
                      const updateData = { ...editProductData };
                      // Remove _id from update data
                      delete updateData._id;
                      res = await apiPatch(`/api/products/${editProduct._id}`, updateData);
                    }
                    
                    if (res.ok) {
                      const updatedProduct = await res.json();
                      console.log('Product updated successfully:', updatedProduct);
                      
                      // Update local state immediately
                      setListings(listings => Array.isArray(listings) ? listings.map(item => 
                        item._id === editProduct._id ? { ...item, ...updatedProduct.product } : item
                      ) : []);
                      
                      // Also refresh from server to ensure consistency
                      try {
                        const refreshRes = await apiGet('/api/products');
                        if (refreshRes.ok) {
                          const refreshData = await refreshRes.json();
                          setListings(refreshData.products || refreshData);
                        }
                      } catch (refreshError) {
                        console.warn('Failed to refresh listings:', refreshError);
                      }
                      
                      setEditProduct(null);
                      showNotification('Product updated successfully! 🎉', 'success');
                    } else {
                      const errorData = await res.json().catch(() => ({}));
                      console.error('Update product error:', res.status, errorData);
                      showNotification(`Failed to update product. ${errorData.error || errorData.message || ''}`, 'error');
                    }
                  } catch (error) {
                    console.error('Update product error:', error);
                    showNotification(`Failed to update product: ${error.message}`, 'error');
                  } finally {
                    setEditLoading(false);
                  }
                }}>
                  {/* Main Image Upload */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold mb-4 text-gray-900">Main Image</h4>
                    <div 
                      className="border-2 border-dashed border-blue-400 rounded-2xl p-10 flex flex-col items-center justify-center bg-blue-50 cursor-pointer hover:bg-blue-100 transition relative"
                      onClick={() => mainImageInputRef.current.click()}
                    >
                      {mainImagePreview ? (
                        <img src={mainImagePreview} alt="Main" className="max-h-48 rounded-lg" />
                      ) : editProduct.mainImage ? (
                        <img
                          src={getImageUrl(editProduct.mainImage)}
                          alt="Main"
                          className="max-h-48 rounded-lg"
                        />
                      ) : (
                        <>
                          <span className="text-5xl mb-2">🖼️</span>
                          <span className="text-gray-600">Click to upload main image</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        ref={mainImageInputRef} 
                        onChange={handleMainImageChange} 
                      />
                    </div>
                  </div>

                  {/* Gallery Images Upload */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold mb-4 text-gray-900">Gallery Images (Min 3 - Max 15)</h4>
                    <div 
                      className="border-2 border-dashed border-blue-400 rounded-2xl p-10 flex flex-col items-center justify-center bg-blue-50 cursor-pointer hover:bg-blue-100 transition relative"
                      onClick={() => galleryInputRef.current.click()}
                    >
                      {galleryPreviews.length > 0 ? (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {galleryPreviews.map((src, idx) => (
                            <img key={idx} src={src} alt={`Gallery ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                          ))}
                        </div>
                      ) : editProduct.galleryImages?.length > 0 ? (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {editProduct.galleryImages.map((img, idx) => (
                            <img
                              key={idx}
                              src={getImageUrl(img)}
                              alt={`Gallery ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          ))}
                        </div>
                      ) : (
                        <>
                          <span className="text-5xl mb-2">🖼️</span>
                          <span className="text-gray-600">Click to upload gallery images</span>
                          <span className="text-xs text-gray-400 mt-1">You can select multiple images</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        className="hidden" 
                        ref={galleryInputRef} 
                        onChange={handleGalleryChange} 
                      />
                    </div>
                  </div>

                  {/* Existing form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block font-semibold mb-1">Name</label>
                      <input className="w-full border rounded px-3 py-2" value={editProductData?.title || ''} onChange={e => setEditProductData(d => ({ ...d, title: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">SKU</label>
                      <input className="w-full border rounded px-3 py-2" value={editProductData?.sku || ''} onChange={e => setEditProductData(d => ({ ...d, sku: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Category</label>
                      <input className="w-full border rounded px-3 py-2" value={editProductData?.stats?.category || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, category: e.target.value } }))} />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Price</label>
                      <input type="number" className="w-full border rounded px-3 py-2" value={editProductData?.price || ''} onChange={e => setEditProductData(d => ({ ...d, price: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Sale Price</label>
                      <input type="number" className="w-full border rounded px-3 py-2" value={editProductData?.salePrice || ''} onChange={e => setEditProductData(d => ({ ...d, salePrice: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Stock Status</label>
                      <select className="w-full border rounded px-3 py-2" value={editProductData?.stockStatus || 'instock'} onChange={e => setEditProductData(d => ({ ...d, stockStatus: e.target.value }))}>
                        <option value="instock">In Stock</option>
                        <option value="outofstock">Out of Stock</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-semibold mb-1">Description</label>
                      <textarea className="w-full border rounded px-3 py-2" rows={3} value={editProductData?.description || ''} onChange={e => setEditProductData(d => ({ ...d, description: e.target.value }))} />
                    </div>
                  </div>
                  {/* Account Stats Section */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold mb-4 text-gray-900">Account Stats</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input className="w-full border rounded px-3 py-2" placeholder="Total Followers" value={editProductData?.stats?.followers || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, followers: e.target.value } }))} />
                      <select className="w-full border rounded px-3 py-2" value={editProductData?.stats?.monetized || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, monetized: e.target.value } }))}>
                        <option value="">Monetized?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      <input className="w-full border rounded px-3 py-2" placeholder="Monthly Income" value={editProductData?.stats?.income || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, income: e.target.value } }))} />
                      <input className="w-full border rounded px-3 py-2" placeholder="Total Number Of Videos" value={editProductData?.stats?.videos || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, videos: e.target.value } }))} />
                      <select className="w-full border rounded px-3 py-2" value={editProductData?.stats?.category || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, category: e.target.value } }))}>
                        <option value="">Account Category</option>
                        <option value="Education & QA">Education & QA</option>
                        <option value="Reviews & How to">Reviews & How to</option>
                        <option value="Movies & Music">Movies & Music</option>
                        <option value="Games">Games</option>
                        <option value="Humor">Humor</option>
                        <option value="Cars & Bikes">Cars & Bikes</option>
                        <option value="Crypto & NFT">Crypto & NFT</option>
                        <option value="Fashion & style">Fashion & style</option>
                        <option value="Fitness & Sports">Fitness & Sports</option>
                        <option value="Food">Food</option>
                        <option value="Luxury & Motivation">Luxury & Motivation</option>
                        <option value="Models & Celebrities">Models & Celebrities</option>
                        <option value="Nature">Nature</option>
                        <option value="Pets & Animals">Pets & Animals</option>
                        <option value="Travel">Travel</option>
                        <option value="YT Shorts & FB reels">YT Shorts & FB reels</option>
                      </select>
                      <input className="w-full border rounded px-3 py-2" placeholder="Total Number Of Views" value={editProductData?.stats?.views || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, views: e.target.value } }))} />
                      <select className="w-full border rounded px-3 py-2" value={editProductData?.stats?.strikes || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, strikes: e.target.value } }))}>
                        <option value="">Strikes or violation?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      <input className="w-full border rounded px-3 py-2" placeholder="Account Created" value={editProductData?.stats?.created || ''} onChange={e => setEditProductData(d => ({ ...d, stats: { ...d.stats, created: e.target.value } }))} />
                    </div>
                  </div>
                  <div className="flex justify-between gap-4 mt-8">
                    <button
                      type="button"
                      className="px-6 py-2 rounded-xl bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition"
                      onClick={() => {
                        showConfirmDialog(
                          'Delete Product',
                          `Are you sure you want to delete "${editProduct.title}"? This action cannot be undone.`,
                          async () => {
                            setEditLoading(true);
                            try {
                              const res = await apiDelete(`/api/products/${editProduct._id}`);
                              if (res.ok) {
                                setListings(listings => listings.filter(item => item._id !== editProduct._id));
                                setEditProduct(null);
                                showNotification('Product deleted successfully! 🗑️', 'success');
                              } else {
                                const errorData = await res.json().catch(() => ({}));
                                console.error('Delete product error:', res.status, errorData);
                                showNotification(`Failed to delete product. ${errorData.error || errorData.message || ''}`, 'error');
                              }
                            } catch (error) {
                              console.error('Delete product error:', error);
                              showNotification(`Failed to delete product: ${error.message}`, 'error');
                            } finally {
                              setEditLoading(false);
                            }
                          },
                          {
                            confirmText: 'Delete Product',
                            cancelText: 'Cancel',
                            type: 'danger'
                          }
                        );
                      }}
                    >
                      Delete
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-xl bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition"
                      disabled={editLoading}
                    >
                      {editLoading ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      );
    case 'Orders':
      return (
        <div className="w-full">
          {/* Bulk actions and filters */}
          <div className="flex flex-wrap items-center gap-2 mb-6 bg-white rounded-2xl shadow-lg px-8 py-5 border border-gray-100">
            <select className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 bg-gray-50 font-semibold" value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
              <option>Bulk actions</option>
              <option>Change status to Processing</option>
              <option>Change status to On hold</option>
              <option>Change status to Completed</option>
              <option>Change status to Cancelled</option>
              <option value="delete">Delete Permanently</option>
            </select>
            <button className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold shadow hover:from-blue-700 hover:to-blue-500 transition text-base" onClick={handleBulkApply}>Apply</button>
            <input className="border rounded-lg px-3 py-2 ml-4 w-72 focus:ring-2 focus:ring-blue-200 bg-gray-50" placeholder="Search orders" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full w-full bg-white rounded-3xl shadow-xl text-base border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-blue-50 to-red-50 text-gray-800 text-lg border-b-2 border-gray-200">
                  <th className="px-4 py-4 text-left"><input type="checkbox" checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0} onChange={e => setSelectedOrders(e.target.checked ? filteredOrders.map(o => o.id) : [])} className="w-5 h-5 accent-blue-600" /></th>
                  <th className="px-4 py-4 text-left">Order</th>
                  <th className="px-4 py-4 text-left">Image</th>
                  <th className="px-4 py-4 text-left">Buyer</th>
                  <th className="px-4 py-4 text-left">Date</th>
                  <th className="px-4 py-4 text-left">Status</th>
                  <th className="px-4 py-4 text-left">Total</th>
                  <th className="px-4 py-4 text-left">Origin</th>
                  <th className="px-4 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o, idx) => (
                  <tr key={o?.id || idx} className={`border-b last:border-b-0 transition hover:bg-blue-50/60 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}> 
                    <td className="px-4 py-4"><input type="checkbox" checked={selectedOrders.includes(o?.id)} onChange={e => setSelectedOrders(e.target.checked ? [...selectedOrders, o?.id] : selectedOrders.filter(id => id !== o?.id))} className="w-5 h-5 accent-blue-600" /></td>
                    <td className="px-4 py-4">
                      <span className="inline-block">
                        <span className="text-blue-700 font-bold underline cursor-pointer hover:text-blue-900 transition" onClick={() => setOrderModal(o)}>{o?.orderCode}</span>
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <a href={`/product/${o?.sku}`} target="_blank" rel="noopener noreferrer">
                        <img src={getImageUrl(o?.product_main_image) || o?.image} alt="Product" className="w-14 h-14 rounded-xl object-cover border-2 border-blue-200 shadow hover:scale-105 transition-transform" />
                      </a>
                    </td>
                    <td className="px-4 py-4 flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-red-400 to-blue-400 text-white font-bold text-lg shadow">
                        {o?.user?.[0]?.toUpperCase() || 'U'}
                      </span>
                      <span className="font-semibold text-gray-800">{o?.user}</span>
                    </td>
                    <td className="px-4 py-4 text-gray-700 font-medium">{o?.date}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-bold shadow-md transition min-h-[2.25rem] tracking-wide
                        ${o?.status === 'on hold' ? 'bg-yellow-100 text-yellow-800' :
                          o?.status === 'nuwanisuru' ? 'bg-black text-white' :
                          o?.status === 'nuwanis' ? 'bg-red-500 text-white' :
                          o?.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                          o?.status === 'refunded' ? 'bg-green-100 text-green-700' :
                          o?.status === 'processing' ? 'bg-orange-100 text-orange-700' :
                          o?.status === 'cancelled' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-700'}`}>{orderStatusDisplayNames[o?.status] || o?.status || '-'}</span>
                    </td>
                    <td className="px-4 py-4 font-bold text-blue-700">{o?.amount}</td>
                    <td className="px-4 py-4 text-gray-600">{o?.origin}</td>
                    <td className="px-4 py-4 text-center">
                      <button
                        title="Delete Order"
                        className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-100 transition"
                        onClick={async () => {
                          console.log('Order to delete:', o); // Debug log
                          if (!o._id) {
                            showNotification('Order ID is missing!', 'error');
                            return;
                          }
                          if (!window.confirm('Are you sure you want to delete this order?')) return;
                          try {
                            const res = await apiDelete(`/api/orders/${o._id}`);
                            if (res.ok) {
                              setOrders(orders => orders.filter(ord => ord._id !== o._id));
                              if (orderModal && orderModal._id === o._id) setOrderModal(null);
                              showNotification('Order deleted successfully! 🗑️', 'success');
                              // Firestore: Optionally mark as deleted or remove
                              try {
                                const conversationsRef = collection(db, 'sellerbuyerconversations');
                                const q = query(conversationsRef, where('order_id', '==', o.order_id));
                                const querySnapshot = await getDocs(q);
                                if (!querySnapshot.empty) {
                                  const conversationDoc = querySnapshot.docs[0];
                                  const conversationRef = doc(db, 'sellerbuyerconversations', conversationDoc.id);
                                  await updateDoc(conversationRef, { deleted: true });
                                  // Optionally, delete messages as well
                                }
                              } catch (err) {
                                // Firestore update is best effort
                                console.warn('Firestore order delete update failed:', err);
                              }
                            } else {
                              showNotification('Failed to delete order.', 'error');
                            }
                          } catch (err) {
                            showNotification('Error deleting order.', 'error');
                          }
                        }}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Order Details Modal */}
          {orderModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-4xl relative border border-gray-100 animate-fadeIn">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl transition" onClick={() => setOrderModal(null)}>&times;</button>
                <h3 className="text-3xl font-extrabold mb-8 flex items-center gap-3 text-gray-900">
                  <span className="inline-block bg-gradient-to-br from-red-100 to-blue-100 text-red-600 rounded-full p-3 shadow"><svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 6h15l-1.5 9h-13z"/><circle cx="9" cy="21" r="1"/><circle cx="19" cy="21" r="1"/></svg></span>
                  Order <span className="text-blue-700">{orderModal.orderCode}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700 w-32">Status:</span>
                      <select className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-200 bg-gray-50 font-semibold" value={orderModal?.status || ''} onChange={e => {
                        setOrders(orders.map(o => o.id === orderModal.id ? { ...o, status: e.target.value } : o));
                        setOrderModal({ ...orderModal, status: e.target.value });
                      }}>{orderStatusOptions.map(opt => <option key={opt} value={opt}>{orderStatusDisplayNames[opt] || opt}</option>)}</select>
                    </div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Date created:</span> <span>{orderModal.date}</span></div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Payment:</span> <span>{orderModal.payment || orderModal.payment_method || '-'}</span></div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Order ID:</span> <span className="truncate max-w-[120px] text-xs text-gray-500">{orderModal.order_id || orderModal.orderId || '-'}</span></div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">MongoDB ID:</span> <span className="truncate max-w-[120px] text-xs text-gray-500">{orderModal.id}</span></div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Customer:</span> <span>{orderModal.user} <span className="text-gray-400">({orderModal.email})</span></span></div>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-6 shadow-inner space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-700 w-32">Origin:</span> <span>{orderModal.origin}</span>
                    </div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Device type:</span> <span>{orderModal.attribution.device}</span></div>
                    <div className="flex items-center gap-2"><span className="font-bold text-gray-700 w-32">Session page views:</span> <span>{orderModal.attribution.sessionViews}</span></div>
                    <div className="font-bold text-gray-700 mt-4 mb-1">Customer history:</div>
                    <ul className="ml-4 text-sm text-gray-600 space-y-1">
                      <li>Total orders: <span className="font-semibold text-gray-900">{orderModal.customerHistory.totalOrders}</span></li>
                      <li>Total revenue: <span className="font-semibold text-gray-900">{orderModal.customerHistory.totalRevenue}</span></li>
                      <li>Average order value: <span className="font-semibold text-gray-900">{orderModal.customerHistory.avgOrder}</span></li>
                    </ul>
                  </div>
                </div>
                <div className="mb-8">
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Billing Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700 w-40">First name:</span>
                      <span>{(orderModal.buyerBillingName || '').split(' ')[0] || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700 w-40">Last name:</span>
                      <span>{(orderModal.buyerBillingName || '').split(' ').slice(1).join(' ') || '-'}</span>
                  </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700 w-40">Account transfer Email address:</span>
                      <span>{orderModal.buyerBillingEmail || orderModal.buyerAccountEmail || orderModal.email || '-'}</span>
                </div>
                    <div className="flex items-center gap-2 md:col-span-2">
                      <span className="font-bold text-gray-700 w-40">Order Notes:</span>
                      <span>{orderModal.orderNotes || orderModal.notes || <span className="text-gray-400">No notes for this order.</span>}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700 w-40">Phone number:</span>
                      <span>{orderModal.phone || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button className="px-7 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition text-lg shadow" onClick={() => setOrderModal(null)}>Close</button>
                  <button
                    className="px-7 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold shadow hover:from-blue-700 hover:to-blue-500 transition text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={async () => {
                      setLoadingMap(prev => ({ ...prev, updatingOrder: true }));
                      try {
                        // Update MongoDB order status first
                        const mongoResponse = await apiPatch(`/api/orders/${orderModal.order_id}`, {
                          status: orderModal.status
                        });

                        if (!mongoResponse.ok) {
                          throw new Error('Failed to update MongoDB order status');
                        }

                        // Update Firestore conversation and messages
                        const conversationsRef = collection(db, 'sellerbuyerconversations');
                        const q = query(conversationsRef, where('order_id', '==', orderModal.order_id));
                        const querySnapshot = await getDocs(q);
                        if (!querySnapshot.empty) {
                          const conversationDoc = querySnapshot.docs[0];
                          const conversationRef = doc(db, 'sellerbuyerconversations', conversationDoc.id);
                          // Update conversation status
                          await updateDoc(conversationRef, {
                            status: orderModal.status
                          });
                          // Update the order message status
                          const messagesCol = collection(db, 'sellerbuyerconversations', conversationDoc.id, 'messages');
                          const messageQuery = query(messagesCol, where('order_id', '==', orderModal.order_id));
                          const messageSnapshot = await getDocs(messageQuery);
                          if (!messageSnapshot.empty) {
                            const messageDoc = messageSnapshot.docs[0];
                            await updateDoc(doc(messagesCol, messageDoc.id), {
                              status: orderModal.status
                            });
                          }
                        }
                        setOrders(orders.map(o => o.id === orderModal.id ? { ...o, status: orderModal.status } : o));
                        setOrderModal(null);
                      } catch (error) {
                        console.error('Error updating order status:', error);
                        showNotification('Failed to update order status. Please try again.', 'error');
                      } finally {
                      setLoadingMap(prev => ({ ...prev, updatingOrder: false }));
                      }
                    }}
                    disabled={loadingMap.updatingOrder}
                  >
                    {loadingMap.updatingOrder && (
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                    )}
                    Update
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    case 'Blog':
      return (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Blog Posts</h2>
            <button className="px-5 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold shadow transition" onClick={() => { setBlogModal(true); setEditBlog(null); setNewBlog({ title: '', description: '', image: '', imageFile: null, content: '', status: 'pending', category: '', author: '' }); }}>+ Add New Blog Post</button>
          </div>
          {blogError && <div className="text-red-500 mb-2">{blogError}</div>}
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full bg-white rounded-xl shadow">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="px-4 py-2">Image</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogLoading ? (
                  <tr><td colSpan={5} className="text-center py-8">Loading...</td></tr>
                ) : blogPostsState.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8">No blog posts found.</td></tr>
                ) : blogPostsState.map(b => (
                  <tr key={b._id} className="border-b last:border-b-0">
                    <td className="px-4 py-2">{b.image ? <img src={b.image} alt="Blog" className="w-16 h-16 object-cover rounded" /> : <span className="text-gray-400">No Image</span>}</td>
                    <td className="px-4 py-2 font-medium">{b.title}</td>
                    <td className="px-4 py-2">{b.description}</td>
                    <td className="px-4 py-2">
                      <select value={b.status} onChange={e => handleBlogStatusChange(b._id, e.target.value)} className="border rounded px-2 py-1">
                        <option value="pending">Pending</option>
                        <option value="published">Published</option>
                      </select>
                    </td>
                    <td className="px-4 py-2 flex gap-2">
                      <button className="px-3 py-1 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition" onClick={() => handleEditBlog(b)}>Edit</button>
                      <button className="px-3 py-1 rounded bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition" onClick={() => handleDeleteBlog(b._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Add/Edit Blog Modal */}
          {blogModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <form className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto relative border border-gray-100 animate-fadeIn" onSubmit={editBlog ? handleUpdateBlog : handleAddBlog}>
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition" onClick={() => setBlogModal(false)} type="button">&times;</button>
                <h3 className="text-3xl font-extrabold mb-8 text-gray-900 flex items-center gap-3">{editBlog ? 'Edit Blog Post' : 'Add New Blog Post'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Title</label>
                    <input className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={newBlog.title} onChange={e => setNewBlog(nb => ({ ...nb, title: e.target.value }))} required />
                </div>
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Category</label>
                    <input className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={newBlog.category} onChange={e => setNewBlog(nb => ({ ...nb, category: e.target.value }))} />
                </div>
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Author</label>
                    <input className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={newBlog.author} onChange={e => setNewBlog(nb => ({ ...nb, author: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Status</label>
                    <select className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={newBlog.status} onChange={e => setNewBlog(nb => ({ ...nb, status: e.target.value }))}>
                      <option value="pending">Pending</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Description</label>
                    <input className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={newBlog.description} onChange={e => setNewBlog(nb => ({ ...nb, description: e.target.value }))} required />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2 text-gray-700">Image</label>
                  <input type="file" accept="image/*" onChange={handleBlogImage} className="mb-2" />
                    {newBlog.image && <img src={newBlog.image} alt="Preview" className="w-32 h-32 object-cover rounded-xl border mb-2" />}
                </div>
                </div>
                <div className="mb-12">
                  <label className="block font-semibold mb-2 text-gray-700">Content</label>
                  <div className="bg-white border rounded-xl p-2">
                    <Editor
                      apiKey={'wrw9d201eh7vqcbpasnn5dbf7jkygopmkml0jgd4dvn78awj'}
                      value={newBlog.content}
                      init={{
                        height: 350,
                        menubar: false,
                        plugins: [
                          'advlist autolink lists link image charmap preview anchor',
                          'searchreplace visualblocks code fullscreen',
                          'insertdatetime media table paste code help wordcount',
                        ],
                        toolbar:
                          'undo redo | formatselect | fontsizeselect | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | removeformat | help',
                        fontsize_formats: '10px 12px 14px 16px 18px 24px 36px 48px',
                        image_title: true,
                        automatic_uploads: true,
                        file_picker_types: 'image',
                        file_picker_callback: function (cb, value, meta) {
                          const input = document.createElement('input');
                          input.setAttribute('type', 'file');
                          input.setAttribute('accept', 'image/*');
                          input.onchange = function () {
                            const file = this.files[0];
                            const reader = new FileReader();
                            reader.onload = function () {
                              const id = 'blobid' + new Date().getTime();
                              const blobCache = window.tinymce.activeEditor.editorUpload.blobCache;
                              const base64 = reader.result.split(',')[1];
                              const blobInfo = blobCache.create(id, file, base64);
                              blobCache.add(blobInfo);
                              cb(blobInfo.blobUri(), { title: file.name });
                            };
                            reader.readAsDataURL(file);
                          };
                          input.click();
                        },
                      }}
                      onEditorChange={content => setNewBlog(nb => ({ ...nb, content }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-8">
                  <button className="px-8 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition text-lg shadow" onClick={() => setBlogModal(false)} type="button">Cancel</button>
                  <button className="px-8 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow transition text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={blogLoading}>{editBlog ? 'Update Blog Post' : 'Add Blog Post'}</button>
                </div>
              </form>
            </div>
          )}
        </div>
      );
    case 'Payments':
      return (
        <div className="w-full">
          <h2 className="text-2xl font-bold mb-8">Available Payment Methods</h2>
          <div className="bg-white shadow divide-y px-0">
            {paymentMethods.map((pm, idx) => (
              <div key={pm.key} className="flex items-center justify-between px-8 py-6">
                <div className="flex items-center gap-4 min-w-0">
                  {pm.icon}
                  <div className="min-w-0">
                    <div className="font-bold text-lg text-gray-900 flex items-center gap-2">{pm.name} {pm.enabled && <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded ml-2">Active</span>}</div>
                    <div className="text-gray-500 text-sm truncate max-w-xl">{pm.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    className={`w-16 h-8 rounded-full border-2 transition-colors duration-200 flex items-center ${pm.enabled ? 'bg-green-400 border-green-500' : 'bg-gray-200 border-gray-300'}`}
                    onClick={() => handleTogglePayment(idx)}
                  >
                    <span
                      className={`inline-block w-7 h-7 rounded-full bg-white shadow transform transition-transform duration-200 ${pm.enabled ? 'translate-x-8' : 'translate-x-0'}`}
                    />
                  </button>
                  <button className="text-blue-600 font-semibold border border-blue-200 px-4 py-1 rounded hover:bg-blue-50 transition" onClick={() => {
                    setManagePaymentModal({ idx, method: pm });
                    setManageDescription(pm.description || '');
                    if (pm.key === 'stripe') {
                      setManageLinks(
                        pm.links && typeof pm.links === 'object'
                          ? Object.entries(pm.links).map(([amount, url]) => `${amount} - ${url}`).join('\n')
                          : ''
                      );
                    } else {
                      setManageLinks('');
                    }
                  }}>Manage</button>
                </div>
              </div>
            ))}
          </div>
          {/* Manage Payment Method Modal */}
          {managePaymentModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-xl relative">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition" onClick={() => setManagePaymentModal(null)}>&times;</button>
                <h3 className="text-2xl font-bold mb-4">Manage Payment Method</h3>
                <div className="mb-4">
                  <label className="block font-semibold mb-2">Description</label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 min-h-[80px] mb-4"
                    value={manageDescription}
                    onChange={e => setManageDescription(e.target.value)}
                  />
                  {managePaymentModal.method.key === 'stripe' && (
                    <div className="mb-4">
                      <label className="block font-semibold mb-2">Amount-to-Link Mapping</label>
                      <textarea
                        className="w-full border rounded-lg px-3 py-2 min-h-[80px]"
                        placeholder="40 - https://buy.stripe.com/..."
                        value={manageLinks}
                        onChange={e => setManageLinks(e.target.value)}
                      />
                      <div className="text-xs text-gray-500 mt-1">One per line, format: <code>amount - https://link</code></div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button className="px-6 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition" onClick={() => setManagePaymentModal(null)}>Cancel</button>
                  <button
                    className="px-6 py-2 rounded-xl bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition"
                    disabled={manageLoading}
                    onClick={async () => {
                      setManageLoading(true);
                      let links = undefined;
                      if (managePaymentModal.method.key === 'stripe') {
                        // Parse manageLinks textarea into an object
                        links = {};
                        manageLinks.split('\n').forEach(line => {
                          const [amount, url] = line.split('-').map(s => s.trim());
                          if (amount && url && /^https?:\/\//.test(url)) {
                            links[amount] = url;
                          }
                        });
                      }
                      const updated = paymentMethods.map((m, i) =>
                        i === managePaymentModal.idx ? { ...m, description: manageDescription, links: links ?? m.links } : m
                      );
                      try {
                        const res = await apiPatch('/api/payment-methods', { 
                          methods: updated.map(({ key, enabled, description, links }) => ({ key, enabled, description, links })) 
                        });
                        
                        if (res.ok) {
                          setPaymentMethods(updated);
                          showNotification('Payment method updated successfully! 💳', 'success');
                        } else {
                          showNotification('Failed to update payment method.', 'error');
                        }
                      } catch (error) {
                        console.error('Payment method update failed:', error);
                        showNotification('Failed to update payment method.', 'error');
                      } finally {
                        setManageLoading(false);
                        setManagePaymentModal(null);
                      }
                    }}
                  >{manageLoading ? 'Updating...' : 'Update'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    case 'SEO':
      const seoTabs = [
        { name: 'Global SEO', icon: '🌐' },
        { name: 'Page SEO', icon: '📄' },
        { name: 'Content SEO', icon: '📝' },
        { name: 'Social Media', icon: '📱' },
        { name: 'Technical', icon: '⚙️' },
        { name: 'Analytics', icon: '📊' },
        { name: 'Sitemap', icon: '🗺️' }, // NEW
      ];

      const handleSeoSave = async (tabName) => {
        setSeoSaving(true);
        setSeoSaveMessage('');
        try {
          const response = await apiPost('/api/seo', seoSettings);
          const data = await response.json();
          if (response.ok) {
            setSeoSaveMessage(`✅ ${data.message}`);
            // Always fetch the latest settings from backend after save
            const fresh = await apiGet('/api/seo');
            const freshData = await fresh.json();
            setSeoSettings(freshData);
          } else {
            setSeoSaveMessage(`❌ Error: ${data.error || 'Failed to save settings'}`);
          }
        } catch (error) {
          console.error('Error saving SEO settings:', error);
          setSeoSaveMessage('❌ Error: Failed to connect to server');
        } finally {
          setSeoSaving(false);
          setTimeout(() => setSeoSaveMessage(''), 3000);
        }
      };

      // Page SEO management functions
      const handlePageSeoSave = async (pagePath, seoSettings) => {
        setPageSeoSaving(true);
        setPageSeoSaveMessage('');
        const { lastUpdated, ...seoSettingsWithoutLastUpdated } = seoSettings;
        try {
          const response = await apiPost('/api/page-seo', { pagePath, seoSettings: seoSettingsWithoutLastUpdated });
          const data = await response.json();
          if (response.ok) {
            setPageSeoSaveMessage(`✅ ${data.message}`);
            // Always fetch the latest page SEO settings from backend after save
            const fresh = await apiGet('/api/page-seo');
            const freshData = await fresh.json();
            setPageSeoSettings(freshData);
          } else {
            setPageSeoSaveMessage(`❌ Error: ${data.error || 'Failed to save page SEO settings'}`);
          }
        } catch (error) {
          console.error('Error saving page SEO settings:', error);
          setPageSeoSaveMessage('❌ Error: Failed to connect to server');
        } finally {
          setPageSeoSaving(false);
          setTimeout(() => setPageSeoSaveMessage(''), 3000);
        }
      };

      const handleExportSeoSettings = async () => {
        try {
          const response = await apiGet('/api/page-seo/export/all');
          const data = await response.json();
          
          if (response.ok) {
            const blob = new Blob([JSON.stringify(data.settings, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `seo-settings-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        } catch (error) {
          console.error('Error exporting SEO settings:', error);
        }
      };

      const handleImportSeoSettings = async () => {
        if (!importData.trim()) {
          setPageSeoSaveMessage('❌ Error: Please enter valid JSON data');
          return;
        }

        try {
          const settings = JSON.parse(importData);
          const response = await apiPost('/api/page-seo/import', { settings });
          
          const data = await response.json();
          
          if (response.ok) {
            setPageSeoSaveMessage(`✅ ${data.message}`);
            setShowImportModal(false);
            setImportData('');
            if (data.settings) {
              setPageSeoSettings(data.settings);
            }
          } else {
            setPageSeoSaveMessage(`❌ Error: ${data.error || 'Failed to import SEO settings'}`);
          }
        } catch (error) {
          console.error('Error importing SEO settings:', error);
          setPageSeoSaveMessage('❌ Error: Invalid JSON data');
        }
      };

      // Add this helper for schema templates
      const schemaTemplates = {
        Product: {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: '',
          description: '',
          image: '',
          offers: { price: '', priceCurrency: 'USD' }
        },
        Article: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: '',
          description: '',
          image: '',
          author: { '@type': 'Person', name: '' }
        },
        FAQ: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: '', acceptedAnswer: { '@type': 'Answer', text: '' } }
          ]
        },
        Organization: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '',
          url: '',
          logo: ''
        }
      };

      return (
        <div className="w-full space-y-8">
          {/* SEO Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-green-500">
              <div className="text-2xl font-bold text-green-600 mb-2">85</div>
              <div className="text-gray-600 font-semibold">SEO Score</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-600 mb-2">1,234</div>
              <div className="text-gray-600 font-semibold">Organic Keywords</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-purple-500">
              <div className="text-2xl font-bold text-purple-600 mb-2">45.2K</div>
              <div className="text-gray-600 font-semibold">Monthly Traffic</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-6 border-t-4 border-orange-500">
              <div className="text-2xl font-bold text-orange-600 mb-2">12</div>
              <div className="text-gray-600 font-semibold">Issues Found</div>
            </div>
          </div>

          {/* SEO Tabs */}
          <div className="bg-white rounded-2xl shadow">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {seoTabs.map((tab) => (
                  <button
                    key={tab.name}
                    className={`py-4 px-1 border-b-2 font-semibold text-base transition flex items-center gap-2
                      ${seoActiveTab === tab.name 
                        ? 'border-blue-500 text-blue-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    onClick={() => setSeoActiveTab(tab.name)}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="p-6">
              {seoActiveTab === 'Global SEO' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Global SEO Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Site Title</label>
                      <input
                        type="text"
                        value={seoSettings.siteTitle}
                        onChange={(e) => setSeoSettings({...seoSettings, siteTitle: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter site title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Site Description</label>
                      <textarea
                        value={seoSettings.siteDescription}
                        onChange={(e) => setSeoSettings({...seoSettings, siteDescription: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        placeholder="Enter site description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Site Keywords</label>
                      <input
                        type="text"
                        value={seoSettings.siteKeywords}
                        onChange={(e) => setSeoSettings({...seoSettings, siteKeywords: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="keyword1, keyword2, keyword3"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Default Meta Title</label>
                      <input
                        type="text"
                        value={seoSettings.defaultTitle}
                        onChange={(e) => setSeoSettings({...seoSettings, defaultTitle: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Default page title"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    {seoSaveMessage && (
                      <div className={`text-sm font-semibold ${seoSaveMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                        {seoSaveMessage}
                      </div>
                    )}
                    <button
                      onClick={() => handleSeoSave('Global SEO')}
                      disabled={seoSaving}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {seoSaving ? 'Saving...' : 'Save Global SEO Settings'}
                    </button>
                  </div>
                </div>
              )}

              {seoActiveTab === 'Social Media' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Social Media SEO</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Title</label>
                      <input
                        type="text"
                        value={seoSettings.ogTitle}
                        onChange={(e) => setSeoSettings({...seoSettings, ogTitle: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Facebook/LinkedIn title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Description</label>
                      <textarea
                        value={seoSettings.ogDescription}
                        onChange={(e) => setSeoSettings({...seoSettings, ogDescription: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        placeholder="Facebook/LinkedIn description"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Image URL</label>
                      <input
                        type="text"
                        value={seoSettings.ogImage}
                        onChange={(e) => setSeoSettings({...seoSettings, ogImage: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Twitter Card Type</label>
                      <select
                        value={seoSettings.twitterCard}
                        onChange={(e) => setSeoSettings({...seoSettings, twitterCard: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="summary">Summary</option>
                        <option value="summary_large_image">Summary Large Image</option>
                        <option value="app">App</option>
                        <option value="player">Player</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => handleSeoSave('Social Media')}
                      disabled={seoSaving}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {seoSaving ? 'Saving...' : 'Save Social Media Settings'}
                    </button>
                  </div>
                </div>
              )}

              {seoActiveTab === 'Technical' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Technical SEO</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Google Analytics ID</label>
                      <input
                        type="text"
                        value={seoSettings.googleAnalytics}
                        onChange={(e) => setSeoSettings({...seoSettings, googleAnalytics: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="G-XXXXXXXXXX"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Google Search Console</label>
                      <input
                        type="text"
                        value={seoSettings.googleSearchConsole}
                        onChange={(e) => setSeoSettings({...seoSettings, googleSearchConsole: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Meta tag content"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Robots.txt Content</label>
                    <textarea
                      value={seoSettings.robotsTxt}
                      onChange={(e) => setSeoSettings({...seoSettings, robotsTxt: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="8"
                      placeholder="User-agent: *\nAllow: /"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seoSettings.sitemapEnabled}
                        onChange={(e) => setSeoSettings({...seoSettings, sitemapEnabled: e.target.checked})}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-700">Enable Sitemap</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seoSettings.canonicalEnabled}
                        onChange={(e) => setSeoSettings({...seoSettings, canonicalEnabled: e.target.checked})}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-700">Enable Canonical URLs</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={seoSettings.schemaEnabled}
                        onChange={(e) => setSeoSettings({...seoSettings, schemaEnabled: e.target.checked})}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-700">Enable Schema Markup</span>
                    </label>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => handleSeoSave('Technical')}
                      disabled={seoSaving}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {seoSaving ? 'Saving...' : 'Save Technical Settings'}
                    </button>
                  </div>
                </div>
              )}

              {seoActiveTab === 'Analytics' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">SEO Analytics & Reports</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Search Performance</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Clicks</span>
                          <span className="font-semibold">12,456</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Impressions</span>
                          <span className="font-semibold">89,234</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average CTR</span>
                          <span className="font-semibold">13.9%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average Position</span>
                          <span className="font-semibold">8.2</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Keywords</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">youtube channels</span>
                          <span className="font-semibold">Position 3</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">buy tiktok account</span>
                          <span className="font-semibold">Position 5</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">instagram accounts</span>
                          <span className="font-semibold">Position 7</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">social media marketplace</span>
                          <span className="font-semibold">Position 12</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">SEO Health Check</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Meta Titles</span>
                        <span className="text-green-600 font-semibold">✓ All pages have titles</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Meta Descriptions</span>
                        <span className="text-green-600 font-semibold">✓ All pages have descriptions</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Image Alt Tags</span>
                        <span className="text-yellow-600 font-semibold">⚠ 12 images missing alt tags</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Page Speed</span>
                        <span className="text-green-600 font-semibold">✓ 92/100 (Good)</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Mobile Friendly</span>
                        <span className="text-green-600 font-semibold">✓ Mobile optimized</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => handleSeoSave('Analytics')}
                      disabled={seoSaving}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {seoSaving ? 'Saving...' : 'Generate SEO Report'}
                    </button>
                  </div>
                </div>
              )}

              {seoActiveTab === 'Page SEO' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900">Page SEO Management</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleExportSeoSettings}
                        className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 transition"
                      >
                        📤 Export All
                      </button>
                      <button
                        onClick={() => setShowImportModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition"
                      >
                        📥 Import
                      </button>
                    </div>
                  </div>

                  {pageSeoSaveMessage && (
                    <div className={`text-sm font-semibold p-3 rounded-lg ${pageSeoSaveMessage.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {pageSeoSaveMessage}
                    </div>
                  )}

                  {/* Website Pages List */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Website Pages</h4>
                    
                    {/* Page Categories */}
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-sm font-semibold text-gray-700">Categories:</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Main Pages</span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Buy Pages</span>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Legal Pages</span>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">User Pages</span>
                      </div>
                    </div>

                    {/* Main Pages */}
                    <div className="mb-6">
                      <h5 className="text-md font-semibold text-gray-800 mb-3">Main Pages</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(pageSeoSettings.pages || {})
                          .filter(([path]) => ['/', '/marketplace', '/about', '/contact', '/blog', '/sell-account'].includes(path))
                          .map(([path, seo]) => (
                          <div key={path} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition cursor-pointer" onClick={() => setSelectedPage({ path, seo, type: 'page' })}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">{path === '/' ? 'Home' : path.slice(1)}</span>
                              <div className="flex gap-1">
                                {seo.noindex && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">NoIndex</span>}
                                {seo.nofollow && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">NoFollow</span>}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{seo.title || 'No title set'}</div>
                            <div className="text-xs text-gray-500">{seo.description ? `${seo.description.substring(0, 60)}...` : 'No description set'}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Buy Pages */}
                    <div className="mb-6">
                      <h5 className="text-md font-semibold text-gray-800 mb-3">Buy Pages</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(pageSeoSettings.pages || {})
                          .filter(([path]) => ['/buy-youtube-channel', '/buy-tiktok-account', '/buy-facebook-page', '/buy-instagram-account'].includes(path))
                          .map(([path, seo]) => (
                          <div key={path} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-green-300 transition cursor-pointer" onClick={() => setSelectedPage({ path, seo, type: 'page' })}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">{path.slice(1).replace(/-/g, ' ')}</span>
                              <div className="flex gap-1">
                                {seo.noindex && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">NoIndex</span>}
                                {seo.nofollow && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">NoFollow</span>}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{seo.title || 'No title set'}</div>
                            <div className="text-xs text-gray-500">{seo.description ? `${seo.description.substring(0, 60)}...` : 'No description set'}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Legal Pages */}
                    <div className="mb-6">
                      <h5 className="text-md font-semibold text-gray-800 mb-3">Legal Pages</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(pageSeoSettings.pages || {})
                          .filter(([path]) => ['/terms', '/refund-policy', '/privacy-policy', '/delivery-faq'].includes(path))
                          .map(([path, seo]) => (
                          <div key={path} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition cursor-pointer" onClick={() => setSelectedPage({ path, seo, type: 'page' })}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">{path.slice(1).replace(/-/g, ' ')}</span>
                              <div className="flex gap-1">
                                {seo.noindex && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">NoIndex</span>}
                                {seo.nofollow && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">NoFollow</span>}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{seo.title || 'No title set'}</div>
                            <div className="text-xs text-gray-500">{seo.description ? `${seo.description.substring(0, 60)}...` : 'No description set'}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* User Pages */}
                    <div className="mb-6">
                      <h5 className="text-md font-semibold text-gray-800 mb-3">User Pages</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(pageSeoSettings.pages || {})
                          .filter(([path]) => ['/login', '/signup', '/profile', '/checkout', '/wishlist', '/cart', '/wise-payment-details', '/reset-password'].includes(path))
                          .map(([path, seo]) => (
                          <div key={path} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-orange-300 transition cursor-pointer" onClick={() => setSelectedPage({ path, seo, type: 'page' })}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">{path.slice(1).replace(/-/g, ' ')}</span>
                              <div className="flex gap-1">
                                {seo.noindex && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">NoIndex</span>}
                                {seo.nofollow && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">NoFollow</span>}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{seo.title || 'No title set'}</div>
                            <div className="text-xs text-gray-500">{seo.description ? `${seo.description.substring(0, 60)}...` : 'No description set'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Product Pages */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Product Pages</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(pageSeoSettings.products || {}).map(([productId, seo]) => (
                        <div key={productId} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition cursor-pointer" onClick={() => setSelectedPage({ path: `product/${productId}`, seo, type: 'product' })}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-gray-900">Product {productId}</span>
                            <div className="flex gap-1">
                              {seo.noindex && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">NoIndex</span>}
                              {seo.nofollow && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">NoFollow</span>}
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 mb-2">{seo.title || 'No title set'}</div>
                          <div className="text-xs text-gray-500">{seo.description ? `${seo.description.substring(0, 60)}...` : 'No description set'}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Page SEO Editor Modal */}
                  {selectedPage && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-xl font-bold text-gray-900">
                            Edit SEO: {selectedPage.path === '/' ? 'Home' : selectedPage.path}
                          </h3>
                          <button
                            onClick={() => setSelectedPage(null)}
                            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                          >
                            ×
                          </button>
                        </div>

                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Page Title</label>
                              <input
                                type="text"
                                value={selectedPage.seo.title || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, title: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter page title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Description</label>
                              <textarea
                                value={selectedPage.seo.description || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, description: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows="3"
                                placeholder="Enter meta description"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Keywords</label>
                              <input
                                type="text"
                                value={selectedPage.seo.keywords || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, keywords: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="keyword1, keyword2, keyword3"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Canonical URL</label>
                              <input
                                type="text"
                                value={selectedPage.seo.canonical || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, canonical: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="https://example.com/page"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Title</label>
                              <input
                                type="text"
                                value={selectedPage.seo.ogTitle || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, ogTitle: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Facebook/LinkedIn title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Description</label>
                              <textarea
                                value={selectedPage.seo.ogDescription || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, ogDescription: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows="3"
                                placeholder="Facebook/LinkedIn description"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Open Graph Image</label>
                              <input
                                type="text"
                                value={selectedPage.seo.ogImage || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, ogImage: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="https://example.com/image.jpg"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Twitter Image</label>
                              <input
                                type="text"
                                value={selectedPage.seo.twitterImage || ''}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, twitterImage: e.target.value}})}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="https://example.com/twitter-image.jpg"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedPage.seo.noindex || false}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, noindex: e.target.checked}})}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm font-semibold text-gray-700">No Index</span>
                            </label>
                            
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedPage.seo.nofollow || false}
                                onChange={(e) => setSelectedPage({...selectedPage, seo: {...selectedPage.seo, nofollow: e.target.checked}})}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm font-semibold text-gray-700">No Follow</span>
                            </label>
                          </div>

                          {/* Insert MetaTagPreview here */}
                          <MetaTagPreview seo={selectedPage.seo} />
                          {/* Structured Data Editor */}
                          <SchemaEditor
                            schema={selectedPage.seo.schema || {}}
                            setSchema={schema => setSelectedPage({ ...selectedPage, seo: { ...selectedPage.seo, schema } })}
                          />
                          {/* Save/Cancel buttons */}
                          <div className="flex justify-end gap-3">
                            <button
                              onClick={() => setSelectedPage(null)}
                              className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg shadow hover:bg-gray-600 transition"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                handlePageSeoSave(selectedPage.path, selectedPage.seo);
                                setSelectedPage(null);
                              }}
                              disabled={pageSeoSaving}
                              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {pageSeoSaving ? 'Saving...' : 'Save Page SEO'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Import Modal */}
                  {showImportModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-xl font-bold text-gray-900">Import SEO Settings</h3>
                          <button
                            onClick={() => {
                              setShowImportModal(false);
                              setImportData('');
                            }}
                            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                          >
                            ×
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">JSON Data</label>
                            <textarea
                              value={importData}
                              onChange={(e) => setImportData(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows="10"
                              placeholder="Paste your SEO settings JSON here..."
                            />
                          </div>

                          <div className="flex justify-end gap-3">
                            <button
                              onClick={() => {
                                setShowImportModal(false);
                                setImportData('');
                              }}
                              className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg shadow hover:bg-gray-600 transition"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleImportSeoSettings}
                              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition"
                            >
                              Import Settings
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {seoActiveTab === 'Content SEO' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Content SEO</h3>
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-lg mb-2">🚧</div>
                    <div className="text-gray-500 font-semibold">Content SEO features coming soon!</div>
                    <div className="text-gray-400 text-sm mt-2">This section will include content optimization tools and keyword analysis.</div>
                  </div>
                </div>
              )}

              {seoActiveTab === 'Sitemap' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">🗺️ Sitemap Management</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <button
                      onClick={handleRegenerateSitemap}
                      disabled={sitemapLoading}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sitemapLoading ? 'Regenerating...' : 'Regenerate Sitemap'}
                    </button>
                    {sitemapMessage && (
                      <span className={`text-sm font-semibold ${sitemapMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>{sitemapMessage}</span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 border-b text-left">URL</th>
                          <th className="px-4 py-2 border-b text-left">Last Modified</th>
                          <th className="px-4 py-2 border-b text-left">Changefreq</th>
                          <th className="px-4 py-2 border-b text-left">Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sitemapData.length === 0 && !sitemapLoading && (
                          <tr><td colSpan={4} className="text-center py-4">No sitemap data found.</td></tr>
                        )}
                        {sitemapData.map((row, idx) => (
                          <tr key={row.url + idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border-b text-blue-700 break-all"><a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a></td>
                            <td className="px-4 py-2 border-b text-xs text-gray-700">{row.lastmod ? new Date(row.lastmod).toLocaleString() : '-'}</td>
                            <td className="px-4 py-2 border-b text-xs text-gray-700">{row.changefreq || '-'}</td>
                            <td className="px-4 py-2 border-b text-xs text-gray-700">{row.priority || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case 'Settings':
      // Enable 2FA: Step 1 - get QR
      const handleStartEnable2FA = async () => {
        setLoading(true); setError(''); setSuccess('');
        try {
          const res = await apiPost('/api/admin/2fa/setup', { password });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to start 2FA setup');
          setQr(data.qr); setBackupCodes(data.backupCodes); setSuccess('Scan QR and enter code below.');
          showNotification('2FA setup initiated! Scan the QR code with your authenticator app.', 'success');
        } catch (e) { 
          setError(e.message); 
          showNotification(`2FA setup failed: ${e.message}`, 'error');
        }
        setLoading(false);
      };
      // Enable 2FA: Step 2 - verify code
      const handleVerifyEnable2FA = async (e) => {
        e.preventDefault(); setLoading(true); setError(''); setSuccess('');
        try {
          const res = await apiPost('/api/admin/2fa/verify', { code });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to enable 2FA');
          setShowEnableModal(false); setCode(''); setPassword(''); setQr(''); setBackupCodes([]); setSuccess('2FA enabled!');
          showNotification('2FA enabled successfully! 🔐', 'success');
        } catch (e) { 
          setError(e.message); 
          showNotification(`Failed to enable 2FA: ${e.message}`, 'error');
        }
        setLoading(false);
      };
      // Disable 2FA
      const handleDisable2FA = async (e) => {
        e.preventDefault(); setLoading(true); setError(''); setSuccess('');
        try {
          const res = await apiPost('/api/admin/2fa/disable', { password, code });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to disable 2FA');
          setShowDisableModal(false); setCode(''); setPassword(''); setSuccess('2FA disabled!');
          showNotification('2FA disabled successfully! 🔓', 'success');
        } catch (e) { 
          setError(e.message); 
          showNotification(`Failed to disable 2FA: ${e.message}`, 'error');
        }
        setLoading(false);
      };
      // Regenerate backup codes handler
      const handleRegenerateBackupCodes = async (e) => {
        e.preventDefault(); setRegenLoading(true); setRegenError(''); setRegenSuccessCodes([]);
        try {
          const res = await apiPost('/api/admin/2fa/regenerate-backup-codes', { password: regenPassword, code: regenCode });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || 'Failed to regenerate backup codes');
          setRegenSuccessCodes(data.backupCodes);
          setRegenPassword(''); setRegenCode('');
          showNotification('Backup codes regenerated successfully! 📝', 'success');
        } catch (e) { 
          setRegenError(e.message); 
          showNotification(`Failed to regenerate backup codes: ${e.message}`, 'error');
        }
        setRegenLoading(false);
      };
      // Download backup codes as txt helper
      function downloadBackupCodes(codes) {
        const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }
      return (
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow p-8 mt-8">
          <h2 className="text-2xl font-bold mb-6">Admin Settings</h2>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <span className="font-semibold text-lg">Two-Factor Authentication (2FA)</span>
              {twoFAStatus === null ? (
                <span className="text-gray-400">Checking...</span>
              ) : twoFAStatus ? (
                <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">Enabled</span>
              ) : (
                <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded">Disabled</span>
              )}
            </div>
            <div className="text-gray-500 mb-2">Protect your admin account with Google Authenticator (TOTP).</div>
            {twoFAStatus && backupCodesRemaining !== undefined && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm text-gray-700">Backup codes left: </span>
                <span className={`font-bold text-sm ${backupCodesRemaining <= 2 ? 'text-red-600' : 'text-green-700'}`}>{backupCodesRemaining}</span>
                {backupCodesRemaining <= 2 && <span className="text-xs text-red-500 font-semibold ml-2">Low! Regenerate soon.</span>}
              </div>
            )}
            {twoFAStatus && (
              <button className="px-5 py-2 rounded bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold shadow hover:from-yellow-500 hover:to-yellow-700 transition mr-3" onClick={() => { setShowRegenerateModal(true); setRegenError(''); setRegenSuccessCodes([]); setRegenPassword(''); setRegenCode(''); }}>Regenerate Backup Codes</button>
            )}
            {twoFAStatus ? (
              <button className="px-6 py-2 rounded bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow hover:from-pink-500 hover:to-red-600 transition" onClick={() => { setShowDisableModal(true); setError(''); setSuccess(''); setCode(''); setPassword(''); }}>Disable 2FA</button>
            ) : (
              <button className="px-6 py-2 rounded bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold shadow hover:from-blue-700 hover:to-blue-500 transition" onClick={() => { setShowEnableModal(true); setError(''); setSuccess(''); setCode(''); setPassword(''); }}>Enable 2FA</button>
            )}
          </div>
          {/* Regenerate Backup Codes Modal */}
          {showRegenerateModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl relative">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition" onClick={() => setShowRegenerateModal(false)}>&times;</button>
                <h3 className="text-xl font-bold mb-4">Regenerate Backup Codes</h3>
                {regenSuccessCodes.length > 0 ? (
                  <div className="text-center">
                    <div className="font-medium mb-2 text-green-700">New backup codes (save these! Old codes are now invalid):</div>
                    <ul className="grid grid-cols-2 gap-2 bg-gray-50 rounded p-2 text-sm font-mono mb-2">
                      {regenSuccessCodes.map(code => <li key={code}>{code}</li>)}
                    </ul>
                    <button type="button" className="px-4 py-1 rounded bg-gray-200 text-gray-700 font-semibold shadow hover:bg-gray-300 transition text-sm mb-2" onClick={() => downloadBackupCodes(regenSuccessCodes)}>Download as .txt</button>
                    <div className="text-xs text-gray-500 mb-2">You will not see these again. Store them securely.</div>
                    <button className="px-6 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition mt-2" onClick={() => { setShowRegenerateModal(false); setRegenSuccessCodes([]); }}>Close</button>
                  </div>
                ) : (
                  <form onSubmit={handleRegenerateBackupCodes} className="flex flex-col gap-4">
                    <input type="password" className="w-full border rounded px-3 py-2" placeholder="Enter your password" value={regenPassword} onChange={e => setRegenPassword(e.target.value)} required />
                    <input type="text" className="w-full border rounded px-3 py-2" placeholder="Enter 2FA or backup code" value={regenCode} onChange={e => setRegenCode(e.target.value)} required />
                    {regenError && <div className="text-red-500 text-sm text-center">{regenError}</div>}
                    <button type="submit" className="px-6 py-2 rounded bg-yellow-600 text-white font-semibold shadow hover:bg-yellow-700 transition" disabled={regenLoading}>{regenLoading ? 'Processing...' : 'Regenerate'}</button>
                  </form>
                )}
              </div>
            </div>
          )}
          {/* Enable 2FA Modal */}
          {showEnableModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl relative">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition" onClick={() => setShowEnableModal(false)}>&times;</button>
                <h3 className="text-xl font-bold mb-4">Enable 2FA</h3>
                <form onSubmit={qr ? handleVerifyEnable2FA : (e) => { e.preventDefault(); handleStartEnable2FA(); }} className="flex flex-col gap-4">
                  {!qr && (
                    <input type="password" className="w-full border rounded px-3 py-2" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required />
                  )}
                  {qr && (
                    <>
                      <div className="mb-2 text-center">
                        <div className="font-medium mb-2">Scan this QR code with Google Authenticator:</div>
                        <img src={qr} alt="QR Code" className="mx-auto mb-4 rounded shadow" style={{ width: 180, height: 180 }} />
                        <div className="font-medium mb-2">Backup codes (save these!):</div>
                        <ul className="grid grid-cols-2 gap-2 bg-gray-50 rounded p-2 text-sm font-mono mb-2">
                          {backupCodes.map(code => <li key={code}>{code}</li>)}
                        </ul>
                        <button type="button" className="px-4 py-1 rounded bg-gray-200 text-gray-700 font-semibold shadow hover:bg-gray-300 transition text-sm mb-2" onClick={() => downloadBackupCodes(backupCodes)}>Download as .txt</button>
                      </div>
                      <input type="text" className="w-full border rounded px-3 py-2" placeholder="Enter 2FA code from app" value={code} onChange={e => setCode(e.target.value)} required />
                    </>
                  )}
                  {error && <div className="text-red-500 text-sm text-center">{error}</div>}
                  {success && <div className="text-green-600 text-sm text-center">{success}</div>}
                  <button type="submit" className="px-6 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition" disabled={loading}>{loading ? 'Processing...' : qr ? 'Enable 2FA' : 'Next'}</button>
                </form>
              </div>
            </div>
          )}
          {/* Disable 2FA Modal */}
          {showDisableModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl relative">
                <button className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold focus:outline-none transition" onClick={() => setShowDisableModal(false)}>&times;</button>
                <h3 className="text-xl font-bold mb-4">Disable 2FA</h3>
                <form onSubmit={handleDisable2FA} className="flex flex-col gap-4">
                  <input type="password" className="w-full border rounded px-3 py-2" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required />
                  <input type="text" className="w-full border rounded px-3 py-2" placeholder="Enter 2FA or backup code" value={code} onChange={e => setCode(e.target.value)} required />
                  {error && <div className="text-red-500 text-sm text-center">{error}</div>}
                  {success && <div className="text-green-600 text-sm text-center">{success}</div>}
                  <button type="submit" className="px-6 py-2 rounded bg-red-600 text-white font-semibold shadow hover:bg-red-700 transition" disabled={loading}>{loading ? 'Processing...' : 'Disable 2FA'}</button>
                </form>
              </div>
            </div>
          )}
        </div>
      );
    case 'Security':
      return (
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">🛡️ Security Analytics</h2>
            <p className="text-gray-600">Monitor security events, failed logins, and suspicious activities</p>
          </div>
          
          {/* Security Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-red-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-red-100 text-red-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Failed Logins</p>
                  <p className="text-2xl font-semibold text-gray-900" id="failedLoginsCount">-</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-orange-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100 text-orange-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Suspicious Activities</p>
                  <p className="text-2xl font-semibold text-gray-900" id="suspiciousActivitiesCount">-</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-yellow-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">High Risk Events</p>
                  <p className="text-2xl font-semibold text-gray-900" id="highRiskEventsCount">-</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow p-6 border-l-4 border-blue-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Blocked IPs</p>
                  <p className="text-2xl font-semibold text-gray-900" id="blockedIPsCount">-</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Security Tabs */}
          <div className="bg-white rounded-xl shadow">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button 
                  className={`py-4 px-1 border-b-2 text-sm font-medium ${
                    securityTab === 'audit-logs' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setSecurityTab('audit-logs')}
                >
                  Audit Logs
                </button>
                <button 
                  className={`py-4 px-1 border-b-2 text-sm font-medium ${
                    securityTab === 'failed-logins' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setSecurityTab('failed-logins')}
                >
                  Failed Logins
                </button>
                <button 
                  className={`py-4 px-1 border-b-2 text-sm font-medium ${
                    securityTab === 'suspicious' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setSecurityTab('suspicious')}
                >
                  Suspicious Activities
                </button>
                <button 
                  className={`py-4 px-1 border-b-2 text-sm font-medium ${
                    securityTab === 'admin-actions' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setSecurityTab('admin-actions')}
                >
                  Admin Actions
                </button>
              </nav>
            </div>
            
            <div className="p-6">
              <div id="security-content">
                {securityLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading security data...</p>
                  </div>
                ) : (
                  <>
                    {securityTab === 'audit-logs' && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Recent Audit Logs</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {auditLogs.map((log, index) => (
                                <tr key={index} className={`${log.isSuspicious ? 'bg-red-50' : ''}`}>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {log.userEmail || 'Anonymous'}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      log.category === 'authentication' ? 'bg-blue-100 text-blue-800' :
                                      log.category === 'admin_action' ? 'bg-purple-100 text-purple-800' :
                                      log.category === 'security' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {log.action}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.ip}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      log.riskLevel === 'critical' ? 'bg-red-100 text-red-800' :
                                      log.riskLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                                      log.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {log.riskLevel}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                                    {log.details}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {securityTab === 'failed-logins' && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Failed Login Attempts</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {failedLogins.map((log, index) => (
                                <tr key={index} className="bg-red-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.userEmail || 'Unknown'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.ip}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{log.userAgent}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.details}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {securityTab === 'suspicious' && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Suspicious Activities</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {suspiciousActivities.map((log, index) => (
                                <tr key={index} className="bg-red-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.userEmail || 'Anonymous'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      {log.action}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.ip}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      log.riskLevel === 'critical' ? 'bg-red-100 text-red-800' :
                                      log.riskLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {log.riskLevel}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{log.details}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {securityTab === 'admin-actions' && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Admin Actions</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {adminActions.map((log, index) => (
                                <tr key={index}>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.userEmail || 'Unknown'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      {log.action}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{log.ip}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      log.riskLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                                      log.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                      {log.riskLevel}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{log.details}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    default:
      // Dashboard Overview
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-red-500 min-h-[140px]">
              <div className="text-3xl font-bold text-red-500 mb-2">{users.length}</div>
              <div className="text-gray-500 font-semibold">Total Users</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-blue-500 min-h-[140px]">
              <div className="text-3xl font-bold text-blue-500 mb-2">${orders.reduce((sum, o) => sum + (parseFloat(o.amount.replace(/[^0-9.]/g, '')) || 0), 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
              <div className="text-gray-500 font-semibold">Total Sales</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-green-500 min-h-[140px]">
              <div className="text-3xl font-bold text-green-500 mb-2">{listings.length}</div>
              <div className="text-gray-500 font-semibold">Total Listings</div>
            </div>
            <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-yellow-500 min-h-[140px]">
              <div className="text-3xl font-bold text-yellow-500 mb-2">{orders.filter(o => o.status === 'completed').length}</div>
              <div className="text-gray-500 font-semibold">Completed Orders</div>
            </div>
          </div>
          {/* Placeholder for charts/analytics */}
          <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-center justify-center min-h-[300px] border border-dashed border-gray-200">
            <div className="text-gray-400 text-xl">[Charts & Analytics Coming Soon]</div>
          </div>
        </>
      );
  }
}

export default function AdminDashboardSite() {
  const { user, isAdmin, logout } = useAuthStore();
  const navigate = useNavigate();
  
  // SECURITY: Session timeout for admin dashboard
  useSessionTimeout();
  
  // Modern Notification System
  const [notification, setNotification] = React.useState(null);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, id: Date.now() });
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };
  
  const hideNotification = () => {
    setNotification(null);
  };

  // Modern Confirmation Dialog System
  const [confirmDialog, setConfirmDialog] = React.useState(null);
  
  const showConfirmDialog = (title, message, onConfirm, options = {}) => {
    setConfirmDialog({
      title,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      ...options
    });
  };
  
  const hideConfirmDialog = () => {
    setConfirmDialog(null);
  };

  // Modern Input Dialog System
  const [inputDialog, setInputDialog] = React.useState(null);
  
  const showInputDialog = (title, message, onConfirm, options = {}) => {
    setInputDialog({
      title,
      message,
      onConfirm: (value) => {
        onConfirm(value);
        setInputDialog(null);
      },
      onCancel: () => setInputDialog(null),
      ...options
    });
  };
  
  const hideInputDialog = () => {
    setInputDialog(null);
  };
  
  const [section, setSection] = React.useState('Dashboard');
  const [orders, setOrders] = React.useState([]);
  const [blogPosts, setBlogPosts] = React.useState([]);
  const [listings, setListings] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [editProduct, setEditProduct] = React.useState(null);
  const [editProductData, setEditProductData] = React.useState(null);
  const [editLoading, setEditLoading] = React.useState(false);
  const [mainImagePreview, setMainImagePreview] = React.useState('');
  const [galleryPreviews, setGalleryPreviews] = React.useState([]);
  const mainImageInputRef = React.useRef();
  const galleryInputRef = React.useRef();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  
  // Security Analytics State
  const [securityTab, setSecurityTab] = React.useState('audit-logs');
  const [securityStats, setSecurityStats] = React.useState({});
  const [auditLogs, setAuditLogs] = React.useState([]);
  const [failedLogins, setFailedLogins] = React.useState([]);
  const [suspiciousActivities, setSuspiciousActivities] = React.useState([]);
  const [adminActions, setAdminActions] = React.useState([]);
  const [securityLoading, setSecurityLoading] = React.useState(false);

  React.useEffect(() => {
    // SECURITY: Verify admin status before loading data
    if (!user || !isAdmin) {
      navigate('/adminlogin');
      return;
    }

    // Fetch real listings from backend with authentication
    apiGet('/api/products', {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            // Unauthorized - redirect to login
            navigate('/adminlogin');
            return;
          }
          throw new Error('Failed to fetch listings');
        }
        return res.json();
      })
      .then(data => setListings(data.products || data))
      .catch(() => setListings([]));

    // Fetch real orders from backend with authentication
    apiGet('/api/orders')
      .then(res => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            // Unauthorized - redirect to login
            navigate('/adminlogin');
            return;
          }
          throw new Error('Failed to fetch orders');
        }
        return res.json();
      })
      .then(data => {
        // Map backend order fields to the format expected by the table
        const mapped = (data || []).map(order => ({
          ...order, // include all backend fields, including _id
          id: order._id, // for legacy compatibility if needed
          order_id: order.order_id, // sequential order number
          payment: order.payment_method || '-',
          orderCode: `#${order.order_id} ${order.buyerUsername || order.buyerBillingName || ''}`,
          user: order.buyerUsername || order.buyerBillingName || '',
          email: order.buyerBillingEmail || order.email,
          amount: `$${order.total?.toFixed(2)}`,
          status: order.status,
          origin: 'Direct',
          date: new Date(order.date).toLocaleDateString(),
          image: getImageUrl(order.product_main_image) || 'https://placehold.co/60x60',
          phone: order.buyerBillingPhone || order.phone,
          sku: order.product_id,
          product: order.product_title,
          notes: '',
          attribution: { device: '', sessionViews: 0 },
          customerHistory: { totalOrders: 0, totalRevenue: '$0.00', avgOrder: '$0.00' },
          buyerBillingName: order.buyerBillingName,
          orderNotes: order.orderNotes,
        }));
        setOrders(mapped);
      })
      .catch(() => setOrders([]));

    // Fetch users for dashboard stats with authentication - REMOVED to prevent conflicts
    // Users are now fetched only when Users section is selected
  }, [user, isAdmin, navigate]);

  // Load security data when Security section is selected
  React.useEffect(() => {
    if (section === 'Security' && user && isAdmin) {
      loadSecurityData();
    }
  }, [section, user, isAdmin]);

  // Function to load security data
  const loadSecurityData = async () => {
    setSecurityLoading(true);
    try {
      // Load security stats
      const statsRes = await apiGet('/api/analytics/security-stats?hours=24');
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setSecurityStats(stats);
        
        // Update the stats cards
        document.getElementById('failedLoginsCount').textContent = stats.failedLogins || 0;
        document.getElementById('suspiciousActivitiesCount').textContent = stats.suspiciousActivities || 0;
        document.getElementById('highRiskEventsCount').textContent = stats.highRiskActivities || 0;
        document.getElementById('blockedIPsCount').textContent = stats.blockedIPs || 0;
      }

      // Load audit logs
      const logsRes = await apiGet('/api/analytics/audit-logs?limit=50');
      if (logsRes.ok) {
        const logs = await logsRes.json();
        setAuditLogs(logs.logs || []);
      }

      // Load failed logins
      const failedRes = await apiGet('/api/analytics/failed-logins?hours=24&limit=50');
      if (failedRes.ok) {
        const failed = await failedRes.json();
        setFailedLogins(failed.failedLogins || []);
      }

      // Load suspicious activities
      const suspiciousRes = await apiGet('/api/analytics/suspicious-activities?hours=24&limit=50');
      if (suspiciousRes.ok) {
        const suspicious = await suspiciousRes.json();
        setSuspiciousActivities(suspicious.suspiciousActivities || []);
      }

      // Load admin actions
      const adminRes = await apiGet('/api/analytics/admin-actions?hours=24&limit=50');
      if (adminRes.ok) {
        const admin = await adminRes.json();
        setAdminActions(admin.adminActions || []);
      }
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setSecurityLoading(false);
    }
  };

  // Calculate dashboard stats
  const totalUsers = users.length;
  const totalListings = listings.length;
  const totalSales = orders.reduce((sum, o) => sum + (parseFloat(o.amount.replace(/[^0-9.]/g, '')) || 0), 0);
  const completedOrders = orders.filter(o => o.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Modern Toast Notification */}
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
      
      {/* Modern Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={true}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          type={confirmDialog.type}
        />
      )}
      
      {/* Modern Input Dialog */}
      {inputDialog && (
        <InputDialog
          isOpen={true}
          title={inputDialog.title}
          message={inputDialog.message}
          onConfirm={inputDialog.onConfirm}
          onCancel={inputDialog.onCancel}
          confirmText={inputDialog.confirmText}
          cancelText={inputDialog.cancelText}
          placeholder={inputDialog.placeholder}
          defaultValue={inputDialog.defaultValue}
        />
      )}
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-100 shadow-lg flex flex-col py-8 px-4">
          <div className="mb-10 text-2xl font-extrabold text-red-600 tracking-tight">Admin Panel</div>
          <nav className="flex-1 space-y-2">
            {sidebarLinks.map(link => (
              <button
                key={link.name}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-lg font-semibold transition
                  ${section === link.name ? 'bg-gradient-to-r from-red-100 to-blue-100 text-red-700 shadow' : 'text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-blue-50'}`}
                onClick={() => setSection(link.name)}
              >
                <span className="text-2xl">{link.icon}</span>
                {link.name}
              </button>
            ))}
          </nav>
        </aside>
        {/* Main Content */}
        <main className="flex-1 flex flex-col items-stretch bg-gray-50">
          {/* Modern Header */}
          <header className="w-full bg-white/80 shadow-lg flex items-center justify-between px-10 py-6 border-b border-gray-100 sticky top-0 z-40" style={{ maxWidth: '1650px', margin: '0 auto', borderRadius: '0 0 2rem 2rem' }}>
            <div className="flex items-center gap-4">
              <img src="/images/ytproud.png" alt="Logo" className="h-10 w-10 rounded-full shadow-md object-contain bg-white" />
              <span className="text-2xl font-extrabold text-gray-900 tracking-tight">Admin Dashboard</span>
              <span className="ml-6 px-4 py-1 rounded-full bg-gradient-to-r from-red-100 to-blue-100 text-red-600 font-bold text-base shadow">{section}</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="relative group">
                <button className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-blue-100 transition group cursor-pointer" title="Profile" onClick={() => setDropdownOpen(v => !v)}>
                  {user?.avatar || user?.photoURL ? (
                    <img
                      src={user.avatar || user.photoURL}
                      alt="avatar"
                      className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-100"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm border-2 border-gray-200 shadow-sm">
                      {(user?.username || user?.displayName || (user?.role === 'admin' ? 'A' : 'U')).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden md:inline text-base font-bold text-gray-900 max-w-[120px] truncate">
                    {user?.role === 'admin' ? 'Admin' : (user?.displayName || user?.username || 'User')}
              </span>
                  <svg className="w-5 h-5 text-gray-500 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 z-50">
                    <button
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl font-semibold"
                      onClick={() => {
                        setDropdownOpen(false);
                        if (logout) logout();
                        navigate('/adminlogin');
                      }}
                    >Logout</button>
                  </div>
                )}
              </div>
            </div>
          </header>
          {/* Section Content */}
          <section
            className="flex-1 px-10 py-10 w-full max-w-full bg-gray-50"
          >
            {section === 'Dashboard' ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                  <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-red-500 min-h-[140px]">
                    <div className="text-3xl font-bold text-red-500 mb-2">{totalUsers}</div>
                    <div className="text-gray-500 font-semibold">Total Users</div>
                  </div>
                  <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-blue-500 min-h-[140px]">
                    <div className="text-3xl font-bold text-blue-500 mb-2">${totalSales.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                    <div className="text-gray-500 font-semibold">Total Sales</div>
                  </div>
                  <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-green-500 min-h-[140px]">
                    <div className="text-3xl font-bold text-green-500 mb-2">{totalListings}</div>
                    <div className="text-gray-500 font-semibold">Total Listings</div>
                  </div>
                  <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start border-t-4 border-yellow-500 min-h-[140px]">
                    <div className="text-3xl font-bold text-yellow-500 mb-2">{completedOrders}</div>
                    <div className="text-gray-500 font-semibold">Completed Orders</div>
                  </div>
                </div>
                {/* Placeholder for charts/analytics */}
                <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-center justify-center min-h-[300px] border border-dashed border-gray-200">
                  <div className="text-gray-400 text-xl">[Charts & Analytics Coming Soon]</div>
                </div>
              </div>
            ) : (
            <SectionContent 
              section={section} 
              orders={orders} 
              setOrders={setOrders} 
              blogPosts={blogPosts} 
              setBlogPosts={setBlogPosts} 
              listings={listings} 
              setListings={setListings} 
              users={users} 
              setUsers={setUsers}
              showNotification={showNotification}
              showConfirmDialog={showConfirmDialog}
              showInputDialog={showInputDialog}
              // Security props
              securityTab={securityTab}
              setSecurityTab={setSecurityTab}
              securityStats={securityStats}
              auditLogs={auditLogs}
              failedLogins={failedLogins}
              suspiciousActivities={suspiciousActivities}
              adminActions={adminActions}
              securityLoading={securityLoading}
            />
            )}
          </section>
        </main>
      </div>
    </div>
  );
} 